import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./View.module.css";
import * as THREE from "three";
import parametricIcon from "../../../assets/parametric.svg";
import transparencyOnIcon from "../../../assets/transparency_on.svg";
import transparencyOffIcon from "../../../assets/transparency_off.svg";
import measureIconLight from "../../../assets/measure_light.svg";
import measureIconDark from "../../../assets/measure_dark.svg";
import isometricIcon from "../../../assets/isometric.svg";
import shareIconLight from "../../../assets/share_light.svg";
import shareIconDark from "../../../assets/share_dark.svg";
import privateRedIcon from "../../../assets/private_red.svg";
import publicGreenIcon from "../../../assets/public_green.svg";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import ModelTabs from "../ModelTabs/ModelTabs";
// Around line 30, add these imports
import noahWaitingLight from "../../../assets/noah_waiting_light.svg";
import noahWaitingDark from "../../../assets/noah_waiting_dark.svg";
import noahDisabledEmptyLight from "../../../assets/noah_disabled_empty_light.svg";
import noahDisabledEmptyDark from "../../../assets/noah_disabled_empty_dark.svg";
import exportIconLight from "../../../assets/export_active_dark.svg";
import exportIconDark from "../../../assets/export_active_light.svg";
import copyIconLight from "../../../assets/copy_item_light.svg";
import copyIconDark from "../../../assets/copy_item_dark.svg";
import checkIconLight from "../../../assets/copy_dark.svg";
import checkIconDark from "../../../assets/copy_light.svg";
import noahDisabledLight from "../../../assets/noah_disabled_light.svg";
import noahDisabledDark from "../../../assets/noah_disabled_dark.svg";
import unfoldIconLight from "../../../assets/chevron_light.svg";
import unfoldIconDark from "../../../assets/chevron_dark.svg";
import dockToLeftIconLight from "../../../assets/dock_to_left_light.svg";
import dockToLeftIconDark from "../../../assets/dock_to_left_dark.svg";
import dockToRightIconLight from "../../../assets/dock_to_right_light.svg";
import dockToRightIconDark from "../../../assets/dock_to_right_dark.svg";
import { BACKEND_URL } from "../../../config/backend";
import loadingIconLight from "../../../assets/loading_light.svg";
import loadingIconDark from "../../../assets/loading_dark.svg";

import { useThemedIcon } from "../../../hooks/useThemedIcon";
interface ViewProps {
  isLeftSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
  onToggleLeftSidebar: () => void;
  onToggleRightSidebar: () => void;
  onToggleModelsSidebar?: () => void; // ADD THIS LINE

  noahGeometry?: any[];
  onShapeSelect?: (selectionData: string) => void;
  currentSelection?: any[];
  onTabRename?: (tabId: string, newName: string) => void;
  fileName?: string;
  onTransparencyChange?: (value: number) => void;
  onColorChange?: (value: string) => void;
  initialTransparency?: number;
  initialColor?: string;
  isLoading?: boolean;
  currentBranch?: string;
  isSharedView?: boolean;
  projectId: string | null | undefined;
  onMeasureDistanceReset?: () => void;
  displayUnit?: string;
  onDisplayUnitChange?: (unit: string) => void;
  isPublic?: boolean;
  shareToken?: string;
  onShareStatusChange?: () => void;
  onMeasurementChange?: (measurement: number) => void;
  isCommandExecuting?: boolean;
  isImporting?: boolean;
  isNewModel?: boolean;
  isAnyButtonLoading?: boolean;
  modelTabs?: Array<{ id: string; name: string; isSharedModel?: boolean }>; // ← UPDATE THIS
  activeTabId?: string | null;
  onTabClick?: (tabId: string) => void;
  onTabClose?: (tabId: string) => void;
  onCloseAllTabs?: () => void;
  onNewModel?: () => void;
  isCreatingNewModel?: boolean;
  isWaitingForFirstPrompt?: boolean;
}

interface SelectionItem {
  type: "edge" | "face";
  objectName: string;
  index: number;
  mesh: THREE.Mesh;
  faceIndex?: number;
  browserCoordinates?: number[];
  browserFaceData?: {
    center: number[];
    normal: number[];
    area: number;
  };
  edgeGroup?: number[]; // ADD THIS LINE
}

const View: React.FC<ViewProps> = ({
  isLeftSidebarOpen,
  isRightSidebarOpen,
  onNewModel, // ← ADD THIS
  onToggleLeftSidebar,
  onToggleRightSidebar,
  onToggleModelsSidebar, // ADD THIS LINE
  noahGeometry = [],
  onShapeSelect,
  currentSelection = [],
  fileName,
  onTransparencyChange,
  onColorChange,
  initialTransparency = 100,
  initialColor = document.documentElement.classList.contains("dark")
    ? "6f6f6f"
    : "8d8d8d",

  isLoading = false,
  currentBranch = "main", // ← ADD THIS LINE (with default)
  isSharedView = false,
  projectId, // ← ADD THIS LINE
  isPublic = false,
  onMeasurementChange,
  shareToken,
  onShareStatusChange,
  onMeasureDistanceReset, // ADD THIS LINE
  isCommandExecuting = false, // ← ADD THIS WITH DEFAULT
  isImporting = false, // ADD THIS LINE
  isNewModel = false, // ADD THIS LINE
  isAnyButtonLoading = false,
  // NEW: Tab management props
  modelTabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onCloseAllTabs,
  onTabRename, // ← ADD THIS LINE HERE
  isCreatingNewModel = false, // ← ADD THIS
  isWaitingForFirstPrompt = false, // ← ADD THIS
}) => {
  const dragValuesRef = useRef({
    hue: 0,
    saturation: 0,
    value: 0,
    transparency: 100,
  });

  const [transparencyValue, setTransparencyValue] =
    useState(initialTransparency);
  const { getIcon } = useThemedIcon();
  const [showSidebarTooltip, setShowSidebarTooltip] = useState(false);
  const [showColorTooltip, setShowColorTooltip] = useState(false);
  const activeTab = modelTabs?.find((tab) => tab.id === activeTabId);
  const isActiveTabShared = activeTab?.isSharedModel === true;
  const lastInteractionRef = useRef<number>(0);
  const AUTO_ROTATE_SPEED = 0.1; // Adjust this for faster/slower spin
  const IDLE_TIME_THRESHOLD = 15000; // 15 seconds in milliseconds
  const copyIconShare = getIcon(copyIconLight, copyIconDark);
  const checkIconShare = getIcon(checkIconLight, checkIconDark);
  const dockToLeftIcon = getIcon(dockToLeftIconLight, dockToLeftIconDark);
  const dockToRightIcon = getIcon(dockToRightIconLight, dockToRightIconDark);
  const shareIcon = getIcon(shareIconLight, shareIconDark);
  const exportIcon = getIcon(exportIconLight, exportIconDark);
  const unfoldIcon = getIcon(unfoldIconLight, unfoldIconDark);
  const loadingIcon = getIcon(loadingIconLight, loadingIconDark);
  const measureIcon = getIcon(measureIconLight, measureIconDark);
  const [displayUnit, setDisplayUnit] = useState<string>("mm");
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [localIsPublic, setLocalIsPublic] = useState(isPublic);
  const [localShareToken, setLocalShareToken] = useState(shareToken);
  const [isCopiedShare, setIsCopiedShare] = useState(false);
  const shareDropdownRef = useRef<HTMLDivElement>(null);
  const noahGeometryRef = useRef(noahGeometry);
  const convertFromMm = useCallback(
    (valueInMm: number, targetUnit: string): string => {
      const conversions: {
        [key: string]: { factor: number; decimals: number; label: string };
      } = {
        mm: { factor: 1.0, decimals: 2, label: "mm" },
        cm: { factor: 0.1, decimals: 2, label: "cm" },
        m: { factor: 0.001, decimals: 4, label: "m" },
        in: { factor: 1 / 25.4, decimals: 3, label: "in" },
        ft: { factor: 1 / 304.8, decimals: 4, label: "ft" },
      };

      const conv = conversions[targetUnit] || conversions["mm"];
      const converted = valueInMm * conv.factor;

      return `${converted.toFixed(conv.decimals)}${conv.label}`;
    },
    [],
  );

  // Add after other constants
  const KEYBOARD_SHORTCUTS = {
    featureTree: {
      mac: "⌘F",
      other: "Ctrl+F",
    },
    models: {
      mac: "⌘M",
      other: "Ctrl+M",
    },
    chat: {
      mac: "⌘J",
      other: "Ctrl+J",
    },
  };

  // Detect if user is on Mac
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  useEffect(() => {
    // console.log("🔄 View: Syncing share status", { isPublic, shareToken });
    setLocalIsPublic(isPublic);
    setLocalShareToken(shareToken);
  }, [isPublic, shareToken]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        shareDropdownRef.current &&
        !shareDropdownRef.current.contains(event.target as Node)
      ) {
        setIsShareDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toFractionalInches = (decimalInches: number): string => {
    const whole = Math.floor(decimalInches);
    const decimal = decimalInches - whole;

    // Common fractions: 1/2, 1/4, 1/8, 1/16, 1/32
    const fractions = [
      [1, 2],
      [1, 4],
      [3, 4],
      [1, 8],
      [3, 8],
      [5, 8],
      [7, 8],
      [1, 16],
      [3, 16],
      [5, 16],
      [7, 16],
      [9, 16],
      [11, 16],
      [13, 16],
      [15, 16],
      [1, 32],
      [3, 32],
      [5, 32],
      [7, 32],
      [9, 32],
      [11, 32],
      [13, 32],
      [15, 32],
      [17, 32],
      [19, 32],
      [21, 32],
      [23, 32],
      [25, 32],
      [27, 32],
      [29, 32],
      [31, 32],
    ];

    // Find closest fraction
    let closest = fractions[0];
    let minDiff = Math.abs(decimal - closest[0] / closest[1]);

    for (const frac of fractions) {
      const diff = Math.abs(decimal - frac[0] / frac[1]);
      if (diff < minDiff) {
        minDiff = diff;
        closest = frac;
      }
    }

    // If very close to whole number, just show whole
    if (minDiff < 0.01) {
      return `${whole}"`;
    }

    if (whole === 0) {
      return `${closest[0]}/${closest[1]}"`;
    }

    return `${whole} ${closest[0]}/${closest[1]}"`;
  };
  // Around line 48, add with other state declarations

  const [isToggleOn, setIsToggleOn] = useState(false);
  const [isOrthographic, setIsOrthographic] = useState(false);
  const [colorValue, setColorValue] = useState(initialColor);
  const [cameraRotation, setCameraRotation] = useState({ x: -30, y: -45 });

  const [measureDistance, setMeasureDistance] = useState<number>(0);

  useEffect(() => {
    setTransparencyValue(initialTransparency);
    // console.log(`🎨 View: Updated transparency to ${initialTransparency}%`);
  }, [initialTransparency]);

  useEffect(() => {
    setColorValue(initialColor);
    // console.log(`🎨 View: Updated color to ${initialColor}`);
  }, [initialColor]);
  const [hueSliderPosition, setHueSliderPosition] = useState(50); // 0-100%
  const [showLeftTooltip, setShowLeftTooltip] = useState(false);
  const [showRightTooltip, setShowRightTooltip] = useState(false);
  const transparencySliderRef = useRef<HTMLDivElement>(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  const thumbWidth = 16; // 1rem
  const padding = 4; // 0.25rem
  // add these near your other refs

  const isFirstGeometryLoad = useRef(true);
  const lastColorChangeRef = useRef<number>(0);
  const colorSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 🔧 CRITICAL: Reset geometry load flag ONLY when switching to a different project
  const lastBranchRef = useRef<string>(currentBranch);

  const updateCameraPosition = useCallback(
    (rotation: { x: number; y: number }, distance?: number) => {
      const currentDistance =
        distance || cameraControlsRef.current.currentDistance;
      const spherical = new THREE.Spherical();

      spherical.radius = currentDistance;
      spherical.phi = THREE.MathUtils.degToRad(90 + rotation.x);
      spherical.theta = THREE.MathUtils.degToRad(-rotation.y);

      // ADDED: Determine camera up vector based on X rotation
      // When looking from below (X > 90 or X < -90), flip the up vector
      const normalizedX = ((rotation.x % 360) + 360) % 360; // Normalize to 0-360
      const shouldFlipUp = normalizedX > 90 && normalizedX < 270;
      const upVector = shouldFlipUp
        ? new THREE.Vector3(0, -1, 0)
        : new THREE.Vector3(0, 1, 0);

      // Update both cameras position
      if (cameraRef.current) {
        cameraRef.current.position.setFromSpherical(spherical);
        cameraRef.current.up.copy(upVector); // ADDED: Set up vector
        cameraRef.current.lookAt(0, 0, 0);
      }

      if (orthoCameraRef.current) {
        orthoCameraRef.current.position.setFromSpherical(spherical);
        orthoCameraRef.current.up.copy(upVector); // ADDED: Set up vector
        orthoCameraRef.current.lookAt(0, 0, 0);
      }

      // Update ref for immediate access
      currentRotationRef.current = rotation;
    },
    [],
  );

  useEffect(() => {
    // Only reset geometry load flag when ACTUALLY navigating between different projects
    // Don't reset when:
    // 1. Initial mount (lastBranchRef is empty)
    // 2. Branch ID just gets a timestamp update (same base branch)

    const lastBranchBase = lastBranchRef.current
      .split("_")
      .slice(0, 3)
      .join("_"); // user_uid_projectid
    const currentBranchBase = currentBranch.split("_").slice(0, 3).join("_");

    if (lastBranchRef.current !== "" && lastBranchBase !== currentBranchBase) {
      // console.log(
      //   `🔄 View: Project switch detected (${lastBranchRef.current} -> ${currentBranch}), resetting geometry load flag`
      // );
      isFirstGeometryLoad.current = true;
    }
    lastBranchRef.current = currentBranch;
  }, [currentBranch]);

  useEffect(() => {
    if (onTransparencyChange) {
      onTransparencyChange(transparencyValue);
    }

    // Save to session storage immediately for recovery
    sessionStorage.setItem(
      `project_${projectId}_transparency`,
      transparencyValue.toString(),
    );

    // Mark that transparency changed recently
    lastColorChangeRef.current = Date.now();

    // Clear any pending save
    if (colorSaveTimeoutRef.current) {
      clearTimeout(colorSaveTimeoutRef.current);
    }

    // Schedule a debounced save after 3 seconds of no changes
    colorSaveTimeoutRef.current = setTimeout(() => {
      // Trigger a save through the parent component
      if ((window as any).__triggerColorSave) {
        // console.log("🎨 Auto-saving after transparency change settled");
        (window as any).__triggerColorSave(colorValue);
      }
    }, 3000);
  }, [transparencyValue, onTransparencyChange, projectId, colorValue]);

  useEffect(() => {
    if (onColorChange) {
      onColorChange(colorValue);
    }

    // Save to session storage immediately for recovery
    sessionStorage.setItem(`project_${projectId}_color`, colorValue);

    // Mark that color changed recently
    lastColorChangeRef.current = Date.now();

    // Clear any pending save
    if (colorSaveTimeoutRef.current) {
      clearTimeout(colorSaveTimeoutRef.current);
    }

    // Schedule a debounced save after 3 seconds of no changes
    colorSaveTimeoutRef.current = setTimeout(() => {
      // Trigger a save through the parent component
      if ((window as any).__triggerColorSave) {
        // console.log("🎨 Auto-saving after color change settled");
        (window as any).__triggerColorSave(colorValue);
      }
    }, 3000); // Wait 3 seconds after last change
  }, [colorValue, onColorChange, projectId]);

  // Reset tooltips when sidebars close
  useEffect(() => {
    if (isLeftSidebarOpen) {
      setShowLeftTooltip(false);
    }
  }, [isLeftSidebarOpen]);

  useEffect(() => {
    noahGeometryRef.current = noahGeometry;
  }, [noahGeometry]);

  useEffect(() => {
    if (transparencySliderRef.current) {
      setSliderWidth(transparencySliderRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (transparencySliderRef.current) {
        setSliderWidth(transparencySliderRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isRightSidebarOpen) {
      setShowRightTooltip(false);
    }
  }, [isRightSidebarOpen]);

  useEffect(() => {
    // Expose measure distance reset to parent
    (window as any).__resetMeasureDistance = () => {
      setMeasureDistance(0);
    };

    return () => {
      delete (window as any).__resetMeasureDistance;
    };
  }, []);

  // Current rotation for immediate updates (separate from React state)
  const currentRotationRef = useRef({ x: -30, y: -45 });
  const [hoveredElements, setHoveredElements] = useState<string[]>([]);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [selectedExportFormat, setSelectedExportFormat] = useState(".STL");

  const [isDraggingTransparency, setIsDraggingTransparency] = useState(false);
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Three.js references
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const orthoCameraRef = useRef<THREE.OrthographicCamera>();
  const frameRef = useRef<number>();
  const prevGeometryRef = useRef<any[]>([]);
  const isOrthographicRef = useRef(false);

  const cameraControlsRef = useRef({
    isRotating: false,
    lastMousePosition: { x: 0, y: 0 },
    mouseDownPosition: { x: 0, y: 0 },
    rotationVelocity: { x: 0, y: 0 },
    inertia: 0.95,
    minDistance: 1,
    maxDistance: Infinity,
    currentDistance: 100,
  });

  // Animation state for smooth transitions
  const animationRef = useRef({
    isAnimating: false,
    startRotation: { x: 0, y: 0 },
    targetRotation: { x: 0, y: 0 },
    startTime: 0,
    duration: 300, // 300ms transition
  });

  // Normalize angle to [-180, 180] range
  const normalizeAngle = useCallback((angle: number) => {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
  }, []);

  // Calculate shortest rotation path
  const calculateShortestPath = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const deltaX = to.x - from.x;
      let deltaY = to.y - from.y;

      // Find shortest Y path
      while (deltaY > 180) deltaY -= 360;
      while (deltaY < -180) deltaY += 360;

      return {
        x: from.x + deltaX,
        y: from.y + deltaY,
      };
    },
    [],
  );

  // Selection state
  const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
  const hoverVisualRef = useRef<THREE.Object3D | null>(null);
  const selectedVisualsRef = useRef<THREE.Object3D[]>([]);

  // References for direct DOM manipulation
  const naviCubeRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef(0);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const colorDropdownRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);

  // STL Loading functions
  const stlLoader = useRef({
    loadSTLFromBase64: function (base64Data: string): THREE.BufferGeometry {
      try {
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const header = new TextDecoder().decode(bytes.slice(0, 80));
        if (header.trim().startsWith("solid")) {
          return this.parseASCIISTL(binaryString);
        } else {
          return this.parseBinarySTL(bytes);
        }
      } catch (error) {
        // console.error("STL parsing error:", error);
        return new THREE.BoxGeometry(20, 15, 10);
      }
    },

    parseASCIISTL: function (stlText: string): THREE.BufferGeometry {
      const vertices: number[] = [];
      const normals: number[] = [];
      const lines = stlText.split("\n");
      let currentNormal = [0, 0, 0];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith("facet normal")) {
          const parts = line.split(/\s+/);
          currentNormal = [
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parseFloat(parts[4]),
          ];
        } else if (line.startsWith("vertex")) {
          const parts = line.split(/\s+/);
          vertices.push(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
          );
          normals.push(currentNormal[0], currentNormal[1], currentNormal[2]);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();
      geometry.computeTangents();

      return geometry;
    },

    parseBinarySTL: function (bytes: Uint8Array): THREE.BufferGeometry {
      const dataView = new DataView(bytes.buffer);
      const triangleCount = dataView.getUint32(80, true);
      const vertices: number[] = [];
      const normals: number[] = [];

      for (let i = 0; i < triangleCount; i++) {
        const offset = 84 + i * 50;
        const nx = dataView.getFloat32(offset, true);
        const ny = dataView.getFloat32(offset + 4, true);
        const nz = dataView.getFloat32(offset + 8, true);

        for (let j = 0; j < 3; j++) {
          const vertexOffset = offset + 12 + j * 12;
          const x = dataView.getFloat32(vertexOffset + 0, true);
          const y = dataView.getFloat32(vertexOffset + 4, true);
          const z = dataView.getFloat32(vertexOffset + 8, true);
          vertices.push(x, y, z);
          normals.push(nx, ny, nz);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();
      return geometry;
    },
  }).current;

  // Update orthographic camera frustum
  const updateOrthographicFrustum = useCallback(() => {
    if (!orthoCameraRef.current || !sceneRef.current || !mountRef.current)
      return;

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;
    const aspect = width / height;

    // Calculate bounding box of all Noah objects
    const box = new THREE.Box3();
    let hasGeometry = false;

    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
        const meshBox = new THREE.Box3().setFromObject(child);
        box.union(meshBox);
        hasGeometry = true;
      }
    });

    if (hasGeometry) {
      const size = box.getSize(new THREE.Vector3());
      const maxDimension = Math.max(size.x, size.y, size.z);
      // Make frustum MUCH larger to ensure visibility
      const frustumSize = maxDimension * 5; // Increased from 3 to 5

      // console.log(
      //   `📐 Setting ortho frustum: ${frustumSize} (maxDim: ${maxDimension})`
      // );

      orthoCameraRef.current.left = (frustumSize * aspect) / -2;
      orthoCameraRef.current.right = (frustumSize * aspect) / 2;
      orthoCameraRef.current.top = frustumSize / 2;
      orthoCameraRef.current.bottom = frustumSize / -2;
      orthoCameraRef.current.near = -2000; // Much larger near/far planes
      orthoCameraRef.current.far = 2000;
    } else {
      // Much larger fallback
      const frustumSize = 1000; // Increased from 500
      orthoCameraRef.current.left = (frustumSize * aspect) / -2;
      orthoCameraRef.current.right = (frustumSize * aspect) / 2;
      orthoCameraRef.current.top = frustumSize / 2;
      orthoCameraRef.current.bottom = frustumSize / -2;
      orthoCameraRef.current.near = -2000;
      orthoCameraRef.current.far = 2000;
    }

    orthoCameraRef.current.updateProjectionMatrix();
  }, []);

  // Update camera position only - simplified and stable

  // Update NaviCube independently to avoid coupling issues
  const updateNaviCube = useCallback((rotation: { x: number; y: number }) => {
    if (naviCubeRef.current) {
      const isAnimating = animationRef.current.isAnimating;
      if (isAnimating) {
        naviCubeRef.current.style.transition = "none";
      } else {
        naviCubeRef.current.style.transition =
          "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      }

      // Just use the rotation values directly - no adjustments
      naviCubeRef.current.style.transform = `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`;
    }
  }, []);

  // Animate camera to target rotation smoothly
  const animateCameraToRotation = useCallback(
    (targetRotation: { x: number; y: number }) => {
      const animation = animationRef.current;
      animation.isAnimating = true;
      animation.startRotation = { ...currentRotationRef.current };
      animation.targetRotation = { ...targetRotation };
      animation.startTime = performance.now();

      // Stop any current inertia when starting animation
      cameraControlsRef.current.rotationVelocity = { x: 0, y: 0 };
    },
    [],
  );

  // Calculate smooth rotation interpolation with proper angle wrapping
  const interpolateRotation = useCallback(
    (
      start: { x: number; y: number },
      target: { x: number; y: number },
      progress: number,
    ) => {
      // Calculate shortest path for Y rotation (horizontal)
      let deltaY = target.y - start.y;
      while (deltaY > 180) deltaY -= 360;
      while (deltaY < -180) deltaY += 360;

      // CHANGED: Don't wrap X rotation - allow it to go past 90
      const deltaX = target.x - start.x;

      // Use CSS-matching cubic-bezier easing (ease-out)
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      return {
        x: start.x + deltaX * easeProgress,
        y: start.y + deltaY * easeProgress,
      };
    },
    [],
  );

  // Handle camera center positioning when sidebars toggle
  const centerCamera = useCallback(() => {
    if (!mountRef.current || !rendererRef.current) return;

    const container = mountRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Always update for smooth tracking (remove aspect ratio check during transitions)

    // console.log(`📐 Centering camera: ${width}x${height}`);

    // Update perspective camera
    if (cameraRef.current) {
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    }

    // Update orthographic camera with proper aspect ratio
    if (orthoCameraRef.current) {
      const aspect = width / height;

      // Calculate bounding box for proper frustum sizing
      const box = new THREE.Box3();
      let hasObjects = false;

      sceneRef.current?.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
          const meshBox = new THREE.Box3().setFromObject(child);
          box.union(meshBox);
          hasObjects = true;
        }
      });

      let frustumSize = 50; // Default size
      if (hasObjects) {
        const size = box.getSize(new THREE.Vector3());
        const maxDimension = Math.max(size.x, size.y, size.z);
        frustumSize = maxDimension * 2;
      }

      orthoCameraRef.current.left = (-frustumSize * aspect) / 2;
      orthoCameraRef.current.right = (frustumSize * aspect) / 2;
      orthoCameraRef.current.top = frustumSize / 2;
      orthoCameraRef.current.bottom = -frustumSize / 2;
      orthoCameraRef.current.near = -1000;
      orthoCameraRef.current.far = 1000;
      orthoCameraRef.current.updateProjectionMatrix();
    }

    // CRITICAL: Update renderer size AND pixel ratio
    // CRITICAL: Update renderer size AND pixel ratio
    // CRITICAL: Update renderer size AND pixel ratio
    rendererRef.current.setSize(width, height);
    rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Force camera position update ONLY if needed
    const currentRotation = currentRotationRef.current;
    const currentDistance = cameraControlsRef.current.currentDistance;

    // Direct camera position update (no additional RAF)
    updateCameraPosition(currentRotation, currentDistance);

    // console.log(`✅ Camera centered for ${width}x${height} viewport`);
  }, [updateCameraPosition]);

  useEffect(() => {
    isOrthographicRef.current = isOrthographic;
  }, [isOrthographic]);

  // Watch for sidebar changes and recenter

  useEffect(() => {
    if (!mountRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      // Instant response - just center the camera
      centerCamera();
    });

    resizeObserver.observe(mountRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [centerCamera]);

  const calculateTriangleNormal = (
    positions: ArrayLike<number>,
    triangleStart: number,
  ): THREE.Vector3 => {
    const v1 = new THREE.Vector3(
      positions[triangleStart],
      positions[triangleStart + 1],
      positions[triangleStart + 2],
    );
    const v2 = new THREE.Vector3(
      positions[triangleStart + 3],
      positions[triangleStart + 4],
      positions[triangleStart + 5],
    );
    const v3 = new THREE.Vector3(
      positions[triangleStart + 6],
      positions[triangleStart + 7],
      positions[triangleStart + 8],
    );

    const edge1 = new THREE.Vector3().subVectors(v2, v1);
    const edge2 = new THREE.Vector3().subVectors(v3, v1);
    const normal = new THREE.Vector3().crossVectors(edge1, edge2);
    normal.normalize();

    return normal;
  };

  const snapToEdgeVertex = useCallback(
    (point: THREE.Vector3, threshold: number = 0.5): THREE.Vector3 => {
      if (!sceneRef.current) return point;

      let closestVertex: THREE.Vector3 | null = null;
      let minDistance = threshold;

      sceneRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
          const edges = new THREE.EdgesGeometry(child.geometry);
          const positions = edges.attributes.position.array;

          for (let i = 0; i < positions.length; i += 3) {
            const vertex = new THREE.Vector3(
              positions[i],
              positions[i + 1],
              positions[i + 2],
            );
            vertex.applyMatrix4(child.matrixWorld);

            const distance = point.distanceTo(vertex);
            if (distance < minDistance) {
              minDistance = distance;
              closestVertex = vertex;
            }
          }
        }
      });

      return closestVertex || point;
    },
    [],
  );

  const calculateEdgeLength = useCallback(
    (mesh: THREE.Mesh, edgeIndex: number): number => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      const start = new THREE.Vector3(
        positions[edgeIndex * 6],
        positions[edgeIndex * 6 + 1],
        positions[edgeIndex * 6 + 2],
      );
      const end = new THREE.Vector3(
        positions[edgeIndex * 6 + 3],
        positions[edgeIndex * 6 + 4],
        positions[edgeIndex * 6 + 5],
      );

      return start.distanceTo(end);
    },
    [],
  );

  const calculateCircleRadius = useCallback(
    (mesh: THREE.Mesh, edgeIndices: number[]): number => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      // Collect ALL unique vertices from the circular edges
      const vertices: THREE.Vector3[] = [];
      const vertexMap = new Map<string, THREE.Vector3>();

      edgeIndices.forEach((edgeIdx) => {
        const start = new THREE.Vector3(
          positions[edgeIdx * 6],
          positions[edgeIdx * 6 + 1],
          positions[edgeIdx * 6 + 2],
        );
        const end = new THREE.Vector3(
          positions[edgeIdx * 6 + 3],
          positions[edgeIdx * 6 + 4],
          positions[edgeIdx * 6 + 5],
        );

        // Use string key to avoid duplicates
        const startKey = `${start.x.toFixed(6)},${start.y.toFixed(
          6,
        )},${start.z.toFixed(6)}`;
        const endKey = `${end.x.toFixed(6)},${end.y.toFixed(6)},${end.z.toFixed(
          6,
        )}`;

        if (!vertexMap.has(startKey)) {
          vertexMap.set(startKey, start);
          vertices.push(start);
        }
        if (!vertexMap.has(endKey)) {
          vertexMap.set(endKey, end);
          vertices.push(end);
        }
      });

      // Calculate center as average of all vertices
      const center = new THREE.Vector3(0, 0, 0);
      vertices.forEach((v) => center.add(v));
      center.divideScalar(vertices.length);

      // Calculate average radius from all vertices (more accurate than using just one)
      let totalRadius = 0;
      vertices.forEach((v) => {
        totalRadius += center.distanceTo(v);
      });
      const radius = totalRadius / vertices.length;

      return radius;
    },
    [],
  );

  // Calculate face data
  const calculateFaceData = useCallback(
    (mesh: THREE.Mesh, faceIndex: number) => {
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal?.array;

      if (!normals) {
        // Fallback for geometry without normals
        const triangleStart = faceIndex * 9;
        const v1 = new THREE.Vector3(
          positions[triangleStart],
          positions[triangleStart + 1],
          positions[triangleStart + 2],
        );
        const v2 = new THREE.Vector3(
          positions[triangleStart + 3],
          positions[triangleStart + 4],
          positions[triangleStart + 5],
        );
        const v3 = new THREE.Vector3(
          positions[triangleStart + 6],
          positions[triangleStart + 7],
          positions[triangleStart + 8],
        );

        const center = new THREE.Vector3()
          .addVectors(v1, v2)
          .add(v3)
          .divideScalar(3);
        center.applyMatrix4(mesh.matrixWorld);

        return {
          center: [center.x, center.y, center.z],
          normal: [0, 1, 0],
          area: 1,
        };
      }

      const triangleStart = faceIndex * 9;
      const clickedNormal = new THREE.Vector3(
        normals[triangleStart],
        normals[triangleStart + 1],
        normals[triangleStart + 2],
      );

      // STEP 1: Find all triangles with same normal
      // STEP 1: Find all triangles with same normal (coplanar)
      // STEP 1: Find all triangles with same normal (coplanar)
      const sameNormalTriangles: number[] = [];

      // More aggressive tolerance for small faces between fillets
      const dotThreshold = 0.999; // ~2.5 degrees tolerance

      const numTriangles = positions.length / 9;

      for (let i = 0; i < numTriangles; i++) {
        const triangleStart = i * 9;
        const triangleNormal = calculateTriangleNormal(
          positions,
          triangleStart,
        );

        const dot = clickedNormal.dot(triangleNormal);
        if (dot > dotThreshold) {
          sameNormalTriangles.push(i);
        }
      }

      // STEP 2: Find connected component starting from clicked triangle
      const connectedTriangles = new Set<number>();
      const toProcess = [faceIndex];
      connectedTriangles.add(faceIndex);

      // Much more generous tolerance for small faces
      const vertexTolerance = 0.05; // Very relaxed for tiny faces between fillets

      while (toProcess.length > 0) {
        const currentTriIndex = toProcess.pop()!;

        // Get vertices of current triangle
        const currentStart = currentTriIndex * 9;
        const currentVertices = [
          new THREE.Vector3(
            positions[currentStart],
            positions[currentStart + 1],
            positions[currentStart + 2],
          ),
          new THREE.Vector3(
            positions[currentStart + 3],
            positions[currentStart + 4],
            positions[currentStart + 5],
          ),
          new THREE.Vector3(
            positions[currentStart + 6],
            positions[currentStart + 7],
            positions[currentStart + 8],
          ),
        ];

        // Check all same-normal triangles for adjacency
        for (const otherTriIndex of sameNormalTriangles) {
          if (connectedTriangles.has(otherTriIndex)) continue;

          const otherStart = otherTriIndex * 9;
          const otherVertices = [
            new THREE.Vector3(
              positions[otherStart],
              positions[otherStart + 1],
              positions[otherStart + 2],
            ),
            new THREE.Vector3(
              positions[otherStart + 3],
              positions[otherStart + 4],
              positions[otherStart + 5],
            ),
            new THREE.Vector3(
              positions[otherStart + 6],
              positions[otherStart + 7],
              positions[otherStart + 8],
            ),
          ];

          // Check if triangles share vertices (are adjacent)
          let sharedVertices = 0;
          for (const currentVertex of currentVertices) {
            for (const otherVertex of otherVertices) {
              if (currentVertex.distanceTo(otherVertex) < vertexTolerance) {
                sharedVertices++;
                break;
              }
            }
          }

          // If triangles share 2+ vertices, they're adjacent
          if (sharedVertices >= 2) {
            connectedTriangles.add(otherTriIndex);
            toProcess.push(otherTriIndex);
          }
        }
      }

      // STEP 3: Calculate center and area of connected component only
      const faceVertices: THREE.Vector3[] = [];
      let totalArea = 0;

      for (const triIndex of connectedTriangles) {
        const triStart = triIndex * 9;
        const v1 = new THREE.Vector3(
          positions[triStart],
          positions[triStart + 1],
          positions[triStart + 2],
        );
        const v2 = new THREE.Vector3(
          positions[triStart + 3],
          positions[triStart + 4],
          positions[triStart + 5],
        );
        const v3 = new THREE.Vector3(
          positions[triStart + 6],
          positions[triStart + 7],
          positions[triStart + 8],
        );

        faceVertices.push(v1, v2, v3);

        const edge1 = new THREE.Vector3().subVectors(v2, v1);
        const edge2 = new THREE.Vector3().subVectors(v3, v1);
        totalArea += edge1.cross(edge2).length() / 2;
      }

      // Calculate center of connected face
      const center = new THREE.Vector3();
      faceVertices.forEach((v) => center.add(v));
      center.divideScalar(faceVertices.length);
      center.applyMatrix4(mesh.matrixWorld);

      const worldNormal = clickedNormal.clone();
      worldNormal.applyMatrix3(
        new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld),
      );
      worldNormal.normalize();

      return {
        center: [center.x, center.y, center.z],
        normal: [worldNormal.x, worldNormal.y, worldNormal.z],
        area: totalArea,
      };
    },
    [],
  );

  // ADD THIS NEW FUNCTION HERE:
  // REPLACE THE ENTIRE groupCircularEdges FUNCTION WITH THIS:
  const groupCircularEdges = useCallback(
    (mesh: THREE.Mesh, clickedEdgeIndex: number) => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      // Get clicked edge
      const clickedStart = new THREE.Vector3(
        positions[clickedEdgeIndex * 6],
        positions[clickedEdgeIndex * 6 + 1],
        positions[clickedEdgeIndex * 6 + 2],
      );
      const clickedEnd = new THREE.Vector3(
        positions[clickedEdgeIndex * 6 + 3],
        positions[clickedEdgeIndex * 6 + 4],
        positions[clickedEdgeIndex * 6 + 5],
      );

      const edgeLength = clickedStart.distanceTo(clickedEnd);
      const edgeMidpoint = clickedStart
        .clone()
        .add(clickedEnd)
        .multiplyScalar(0.5);

      // console.log(
      //   `🔍 Clicked edge ${clickedEdgeIndex}, length: ${edgeLength.toFixed(3)}`
      // );

      if (edgeLength > 5.0) {
        return [clickedEdgeIndex];
      }

      // Step 1: Find ALL edges with similar length
      const candidateEdges: number[] = [];
      const lengthTolerance = Math.max(0.4, edgeLength * 0.25); // More generous

      for (let i = 0; i < positions.length; i += 6) {
        const edgeIndex = i / 6;
        const start = new THREE.Vector3(
          positions[i],
          positions[i + 1],
          positions[i + 2],
        );
        const end = new THREE.Vector3(
          positions[i + 3],
          positions[i + 4],
          positions[i + 5],
        );
        const length = start.distanceTo(end);

        if (Math.abs(length - edgeLength) < lengthTolerance) {
          candidateEdges.push(edgeIndex);
        }
      }

      if (candidateEdges.length < 8) {
        return [clickedEdgeIndex];
      }

      // Step 2: Build connectivity graph - which edges are adjacent to each other
      const adjacencyMap = new Map<number, number[]>();
      const connectionTolerance = edgeLength * 1.1; // Edges that share vertices

      candidateEdges.forEach((edgeIdx) => {
        adjacencyMap.set(edgeIdx, []);

        const start1 = new THREE.Vector3(
          positions[edgeIdx * 6],
          positions[edgeIdx * 6 + 1],
          positions[edgeIdx * 6 + 2],
        );
        const end1 = new THREE.Vector3(
          positions[edgeIdx * 6 + 3],
          positions[edgeIdx * 6 + 4],
          positions[edgeIdx * 6 + 5],
        );

        candidateEdges.forEach((otherIdx) => {
          if (edgeIdx === otherIdx) return;

          const start2 = new THREE.Vector3(
            positions[otherIdx * 6],
            positions[otherIdx * 6 + 1],
            positions[otherIdx * 6 + 2],
          );
          const end2 = new THREE.Vector3(
            positions[otherIdx * 6 + 3],
            positions[otherIdx * 6 + 4],
            positions[otherIdx * 6 + 5],
          );

          // Check if edges share a vertex (are connected)
          const connected =
            start1.distanceTo(start2) < connectionTolerance ||
            start1.distanceTo(end2) < connectionTolerance ||
            end1.distanceTo(start2) < connectionTolerance ||
            end1.distanceTo(end2) < connectionTolerance;

          if (connected) {
            adjacencyMap.get(edgeIdx)!.push(otherIdx);
          }
        });
      });

      // Step 3: Find the connected component containing the clicked edge
      const connectedComponent = new Set<number>();
      const toVisit = [clickedEdgeIndex];

      while (toVisit.length > 0) {
        const currentEdge = toVisit.pop()!;
        if (connectedComponent.has(currentEdge)) continue;

        connectedComponent.add(currentEdge);

        // Add all adjacent edges to visit queue
        const adjacent = adjacencyMap.get(currentEdge) || [];
        adjacent.forEach((adjEdge) => {
          if (!connectedComponent.has(adjEdge)) {
            toVisit.push(adjEdge);
          }
        });
      }

      // console.log(
      //   `🔍 Found connected component: ${connectedComponent.size} edges`
      // );

      if (connectedComponent.size < 6) {
        return [clickedEdgeIndex];
      }

      // Step 4: Verify this is actually a circle by checking if edges form a ring
      const componentEdges = Array.from(connectedComponent);

      // Calculate center from this connected component
      const componentMidpoints: THREE.Vector3[] = [];
      componentEdges.forEach((edgeIdx) => {
        const start = new THREE.Vector3(
          positions[edgeIdx * 6],
          positions[edgeIdx * 6 + 1],
          positions[edgeIdx * 6 + 2],
        );
        const end = new THREE.Vector3(
          positions[edgeIdx * 6 + 3],
          positions[edgeIdx * 6 + 4],
          positions[edgeIdx * 6 + 5],
        );
        componentMidpoints.push(start.clone().add(end).multiplyScalar(0.5));
      });

      const componentCenter = new THREE.Vector3(0, 0, 0);
      componentMidpoints.forEach((mp) => componentCenter.add(mp));
      componentCenter.divideScalar(componentMidpoints.length);

      const componentRadius = componentCenter.distanceTo(edgeMidpoint);

      // Step 5: Filter to only edges that are actually part of the circle
      // (eliminates edges that might be connected but not circular)
      const circularEdges: number[] = [];
      const radiusTolerance = Math.max(1.5, componentRadius * 0.2);

      componentEdges.forEach((edgeIdx) => {
        const start = new THREE.Vector3(
          positions[edgeIdx * 6],
          positions[edgeIdx * 6 + 1],
          positions[edgeIdx * 6 + 2],
        );
        const end = new THREE.Vector3(
          positions[edgeIdx * 6 + 3],
          positions[edgeIdx * 6 + 4],
          positions[edgeIdx * 6 + 5],
        );
        const midpoint = start.clone().add(end).multiplyScalar(0.5);
        const distanceFromCenter = componentCenter.distanceTo(midpoint);

        if (Math.abs(distanceFromCenter - componentRadius) < radiusTolerance) {
          circularEdges.push(edgeIdx);
        }
      });

      // Step 6: Final check - only use if we have enough edges for a circle
      // Step 6: Final check - verify it's actually circular, not just connected
      if (circularEdges.length >= 8) {
        // Calculate average angle between consecutive edges
        const angles: number[] = [];

        for (let i = 0; i < Math.min(circularEdges.length, 12); i++) {
          const edgeIdx1 = circularEdges[i];
          const edgeIdx2 = circularEdges[(i + 1) % circularEdges.length];

          const mid1 = new THREE.Vector3(
            (positions[edgeIdx1 * 6] + positions[edgeIdx1 * 6 + 3]) / 2,
            (positions[edgeIdx1 * 6 + 1] + positions[edgeIdx1 * 6 + 4]) / 2,
            (positions[edgeIdx1 * 6 + 2] + positions[edgeIdx1 * 6 + 5]) / 2,
          );
          const mid2 = new THREE.Vector3(
            (positions[edgeIdx2 * 6] + positions[edgeIdx2 * 6 + 3]) / 2,
            (positions[edgeIdx2 * 6 + 1] + positions[edgeIdx2 * 6 + 4]) / 2,
            (positions[edgeIdx2 * 6 + 2] + positions[edgeIdx2 * 6 + 5]) / 2,
          );

          const v1 = mid1.clone().sub(componentCenter).normalize();
          const v2 = mid2.clone().sub(componentCenter).normalize();
          const angle = Math.acos(Math.max(-1, Math.min(1, v1.dot(v2))));
          angles.push(angle);
        }

        const avgAngle = angles.reduce((a, b) => a + b, 0) / angles.length;
        const angleVariance =
          angles.reduce(
            (sum, angle) => sum + Math.pow(angle - avgAngle, 2),
            0,
          ) / angles.length;

        // High variance = not circular (like a square has 4 big angles at corners)
        if (angleVariance > 0.02) {
          // console.log(
          //   `🔍 Not circular - angle variance too high (${angleVariance.toFixed(
          //     4
          //   )})`
          // );
          return [clickedEdgeIndex];
        }

        // console.log(
        //   `🔍 Found complete circular edge: ${
        //     circularEdges.length
        //   } edges, variance: ${angleVariance.toFixed(4)}`
        // );
        return circularEdges.sort((a, b) => a - b);
      }

      // console.log(
      //   `🔍 Not enough circular edges (${circularEdges.length}), using single edge`
      // );
      return [clickedEdgeIndex];
    },
    [],
  );

  const drawMeasurementPoint = useCallback((point: THREE.Vector3) => {
    if (!sceneRef.current) return;

    // Create white ring circle (like hueThumb)
    // Create white ring circle (like hueThumb)
    const ringGeometry = new THREE.RingGeometry(0.35, 0.5, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 1.0,
      depthTest: false, // Always render on top
      depthWrite: false, // Don't write to depth buffer
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.renderOrder = 999; // Render last (on top of everything)
    ring.position.copy(point);

    // Make ring face the camera
    if (cameraRef.current) {
      ring.lookAt(cameraRef.current.position);
    }

    ring.userData = { isMeasurementVisual: true };
    sceneRef.current.add(ring);
  }, []);

  const drawMeasurementLine = useCallback(
    (start: THREE.Vector3, end: THREE.Vector3) => {
      if (!sceneRef.current) return;

      // Clear previous measurement visuals
      sceneRef.current.children = sceneRef.current.children.filter(
        (child) => !child.userData?.isMeasurementVisual,
      );

      // Draw white 1px line
      // Draw white 1px line
      const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
      const material = new THREE.LineBasicMaterial({
        color: 0xffffff,
        linewidth: 1,
        transparent: true,
        opacity: 1.0,
        depthTest: false, // Always render on top
        depthWrite: false, // Don't write to depth buffer
      });
      const line = new THREE.Line(geometry, material);
      line.renderOrder = 999; // Render last (on top of everything)
      line.userData = { isMeasurementVisual: true };
      sceneRef.current.add(line);

      // Draw endpoint markers
      drawMeasurementPoint(start);
      drawMeasurementPoint(end);
    },
    [drawMeasurementPoint],
  );

  // Visual creation functions
  const createEdgeVisual = useCallback(
    (
      mesh: THREE.Mesh,
      edgeIndex: number,
      color?: number,
      linewidth: number = 3,
      opacity: number = 1.0, // ← ADD THIS PARAMETER
    ) => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      const singleEdgePositions = [
        positions[edgeIndex * 6],
        positions[edgeIndex * 6 + 1],
        positions[edgeIndex * 6 + 2],
        positions[edgeIndex * 6 + 3],
        positions[edgeIndex * 6 + 4],
        positions[edgeIndex * 6 + 5],
      ];

      const singleEdgeGeometry = new THREE.BufferGeometry();
      singleEdgeGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(singleEdgePositions, 3),
      );

      // Use original mesh color if no color provided
      const edgeColor =
        color !== undefined
          ? color
          : (mesh.material as THREE.MeshStandardMaterial).color
              .clone()
              .multiplyScalar(1.3); // ← CHANGED FROM 0.3 TO 1.3 (BRIGHTER!)

      const edgeMaterial = new THREE.LineBasicMaterial({
        color: edgeColor,
        linewidth: linewidth,
        transparent: true, // ← ADD THIS
        opacity: opacity, // ← ADD THIS
      });
      const edgeLine = new THREE.LineSegments(singleEdgeGeometry, edgeMaterial);

      edgeLine.position.copy(mesh.position);
      edgeLine.rotation.copy(mesh.rotation);
      edgeLine.scale.copy(mesh.scale);
      edgeLine.userData = { isSelectionVisual: true };

      return edgeLine;
    },
    [],
  );

  // ADD THIS NEW FUNCTION HERE:
  const createEdgeGroupVisual = useCallback(
    (
      mesh: THREE.Mesh,
      edgeIndices: number[],
      color?: number,
      linewidth: number = 3,
      opacity: number = 1.0, // ← ADD THIS PARAMETER
    ) => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      const groupEdgePositions: number[] = [];

      edgeIndices.forEach((edgeIndex) => {
        const startIdx = edgeIndex * 6;
        groupEdgePositions.push(
          positions[startIdx],
          positions[startIdx + 1],
          positions[startIdx + 2],
          positions[startIdx + 3],
          positions[startIdx + 4],
          positions[startIdx + 5],
        );
      });

      const groupGeometry = new THREE.BufferGeometry();
      groupGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(groupEdgePositions, 3),
      );

      // Use original mesh color if no color provided
      const edgeColor =
        color !== undefined
          ? color
          : (mesh.material as THREE.MeshStandardMaterial).color
              .clone()
              .multiplyScalar(1.3); // ← CHANGED FROM 0.3 TO 1.3 (BRIGHTER!)

      const edgeMaterial = new THREE.LineBasicMaterial({
        color: edgeColor,
        linewidth: linewidth,
        transparent: true, // ← ADD THIS
        opacity: opacity, // ← ADD THIS
      });

      const edgeLine = new THREE.LineSegments(groupGeometry, edgeMaterial);
      edgeLine.position.copy(mesh.position);
      edgeLine.rotation.copy(mesh.rotation);
      edgeLine.scale.copy(mesh.scale);
      edgeLine.userData = { isSelectionVisual: true };

      return edgeLine;
    },
    [],
  );

  const createFaceVisual = useCallback(
    (
      mesh: THREE.Mesh,
      faceIndex: number,
      color?: number,
      opacity: number = 0.5,
    ) => {
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal?.array;

      if (!normals) return null;

      const triangleStart = faceIndex * 9;
      const clickedNormal = new THREE.Vector3(
        normals[triangleStart],
        normals[triangleStart + 1],
        normals[triangleStart + 2],
      );

      // Find ONLY connected triangles (not just same normal)
      const connectedTriangles = new Set<number>();
      const toProcess = [faceIndex];
      connectedTriangles.add(faceIndex);
      const tolerance = 0.0001;

      while (toProcess.length > 0) {
        const currentTriIndex = toProcess.pop()!;
        const currentStart = currentTriIndex * 9;
        const currentVertices = [
          new THREE.Vector3(
            positions[currentStart],
            positions[currentStart + 1],
            positions[currentStart + 2],
          ),
          new THREE.Vector3(
            positions[currentStart + 3],
            positions[currentStart + 4],
            positions[currentStart + 5],
          ),
          new THREE.Vector3(
            positions[currentStart + 6],
            positions[currentStart + 7],
            positions[currentStart + 8],
          ),
        ];

        for (let i = 0; i < positions.length; i += 9) {
          const triIndex = i / 9;
          if (connectedTriangles.has(triIndex)) continue;

          const triangleNormal = new THREE.Vector3(
            normals[i],
            normals[i + 1],
            normals[i + 2],
          );
          if (clickedNormal.distanceTo(triangleNormal) >= tolerance) continue;

          const otherVertices = [
            new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]),
            new THREE.Vector3(
              positions[i + 3],
              positions[i + 4],
              positions[i + 5],
            ),
            new THREE.Vector3(
              positions[i + 6],
              positions[i + 7],
              positions[i + 8],
            ),
          ];

          let sharedVertices = 0;
          for (const currentVertex of currentVertices) {
            for (const otherVertex of otherVertices) {
              if (currentVertex.distanceTo(otherVertex) < 0.001) {
                sharedVertices++;
                break;
              }
            }
          }

          if (sharedVertices >= 2) {
            connectedTriangles.add(triIndex);
            toProcess.push(triIndex);
          }
        }
      }

      // Build geometry from ONLY connected triangles
      const sameFaceVertices: number[] = [];
      for (const triIndex of connectedTriangles) {
        const i = triIndex * 9;
        sameFaceVertices.push(
          positions[i],
          positions[i + 1],
          positions[i + 2],
          positions[i + 3],
          positions[i + 4],
          positions[i + 5],
          positions[i + 6],
          positions[i + 7],
          positions[i + 8],
        );
      }

      if (sameFaceVertices.length === 0) return null;

      const faceGeometry = new THREE.BufferGeometry();
      faceGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(sameFaceVertices, 3),
      );

      const indices = [];
      for (let i = 0; i < sameFaceVertices.length / 3; i += 3) {
        indices.push(i, i + 1, i + 2);
      }
      faceGeometry.setIndex(indices);

      const faceColor =
        color !== undefined
          ? color
          : Array.isArray(mesh.material)
            ? new THREE.Color(0x333333)
            : (mesh.material as THREE.MeshStandardMaterial).color
                .clone()
                .multiplyScalar(1.3);

      const faceMaterial = new THREE.MeshBasicMaterial({
        color: faceColor,
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
      });

      const faceMesh = new THREE.Mesh(faceGeometry, faceMaterial);
      faceMesh.position.copy(mesh.position);
      faceMesh.rotation.copy(mesh.rotation);
      faceMesh.scale.copy(mesh.scale);
      faceMesh.userData = { isSelectionVisual: true };

      return faceMesh;
    },
    [],
  );

  // Clear visual helpers
  const clearHoverVisual = useCallback(() => {
    if (hoverVisualRef.current && sceneRef.current) {
      sceneRef.current.remove(hoverVisualRef.current);
      hoverVisualRef.current = null;
    }
  }, []);

  const clearSelectedVisuals = useCallback(() => {
    if (sceneRef.current) {
      selectedVisualsRef.current.forEach((visual) => {
        sceneRef.current!.remove(visual);
      });
      selectedVisualsRef.current = [];
    }
  }, []);

  // Update selection visuals
  const updateSelectionVisuals = useCallback(() => {
    clearSelectedVisuals();

    selectedItems.forEach((item) => {
      let visual: THREE.Object3D | null = null;

      if (item.type === "edge") {
        if (item.edgeGroup && item.edgeGroup.length > 1) {
          visual = createEdgeGroupVisual(
            item.mesh,
            item.edgeGroup,
            undefined,
            8,
          );
        } else {
          visual = createEdgeVisual(item.mesh, item.index, undefined, 8);
        }
      } else if (item.type === "face") {
        visual = createFaceVisual(item.mesh, item.index, undefined, 0.5);
      }

      if (visual && sceneRef.current) {
        sceneRef.current.add(visual);
        selectedVisualsRef.current.push(visual);
      }
    });
  }, [selectedItems, createEdgeVisual, createFaceVisual, clearSelectedVisuals]);

  // Sync with parent selection
  useEffect(() => {
    if (currentSelection.length === 0 && selectedItems.length > 0) {
      setSelectedItems([]);
    }
  }, [currentSelection]);

  // Initialize scene - only once
  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    // Read background color from CSS variable
    const bgColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-white")
      .trim();
    scene.background = new THREE.Color(bgColor || "#ffffff");
    sceneRef.current = scene;

    // Create perspective camera

    const camera = new THREE.PerspectiveCamera(
      50,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1, // ← Near plane stays small (can zoom in close)
      50000, // ← Far plane MUCH larger (can see large models from far away)
    );
    cameraRef.current = camera;

    // Create orthographic camera
    const aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
    const frustumSize = 100;
    const orthoCamera = new THREE.OrthographicCamera(
      (frustumSize * aspect) / -2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      frustumSize / -2,
      0.1, // ← Near
      50000, // ← Far: MUCH larger!
    );
    orthoCameraRef.current = orthoCamera;

    // Set initial camera position
    updateCameraPosition(cameraRotation, 100); // ← Keep 100 as the base default

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(
      mountRef.current.clientWidth,
      mountRef.current.clientHeight,
    );
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    rendererRef.current = renderer;

    // 1. ENVIRONMENT LIGHTING - Soft, even base illumination
    const ambientLight = new THREE.AmbientLight(0xf8f9fa, 0.3);
    scene.add(ambientLight);

    // 2. KEY LIGHT - Primary directional light
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
    keyLight.position.set(100, 120, 80);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 4096;
    keyLight.shadow.mapSize.height = 4096;
    keyLight.shadow.camera.near = 0.1;
    keyLight.shadow.camera.far = 1000;
    keyLight.shadow.camera.left = -200;
    keyLight.shadow.camera.right = 200;
    keyLight.shadow.camera.top = 200;
    keyLight.shadow.camera.bottom = -200;
    keyLight.shadow.bias = -0.0005;
    keyLight.shadow.normalBias = 0.02;
    keyLight.shadow.radius = 4;
    keyLight.userData = { originalIntensity: 1.2, type: "key" };
    scene.add(keyLight);

    // 3. FILL LIGHT - Softer light to reduce harsh shadows
    const fillLight = new THREE.DirectionalLight(0xe8f4f8, 0.6);
    fillLight.position.set(-80, 60, 40);
    fillLight.castShadow = true;
    fillLight.shadow.mapSize.width = 2048;
    fillLight.shadow.mapSize.height = 2048;
    fillLight.shadow.camera.near = 0.1;
    fillLight.shadow.camera.far = 800;
    fillLight.shadow.camera.left = -150;
    fillLight.shadow.camera.right = 150;
    fillLight.shadow.camera.top = 150;
    fillLight.shadow.camera.bottom = -150;
    fillLight.shadow.bias = -0.0003;
    fillLight.shadow.radius = 6;
    fillLight.userData = { originalIntensity: 0.6, type: "fill" };
    scene.add(fillLight);

    // 4. RIM LIGHT - Back light for edge definition
    const rimLight = new THREE.DirectionalLight(0xfff8e1, 0.8);
    rimLight.position.set(-60, 40, -100);
    rimLight.userData = { originalIntensity: 0.8, type: "rim" };
    scene.add(rimLight);

    // 5. TOP LIGHT - Overhead illumination
    const topLight = new THREE.DirectionalLight(0xf0f8ff, 0.5);
    topLight.position.set(0, 150, 0);
    topLight.userData = { originalIntensity: 0.5, type: "top" };
    scene.add(topLight);

    // 6. HEMISPHERE LIGHT - Sky/ground lighting
    const hemisphereLight = new THREE.HemisphereLight(0xb8d4f0, 0x8b7355, 0.4);
    hemisphereLight.position.set(0, 100, 0);
    hemisphereLight.userData = { originalIntensity: 0.4, type: "hemisphere" };
    scene.add(hemisphereLight);

    mountRef.current.appendChild(renderer.domElement);

    const handleResize = () => {
      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Set new debounced timeout
      resizeTimeoutRef.current = setTimeout(() => {
        centerCamera();
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    // Animation loop with inertia and smooth transitions
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);

      const controls = cameraControlsRef.current;
      const animation = animationRef.current;

      // Handle smooth NaviCube click animations
      // Handle smooth NaviCube click animations
      if (animation.isAnimating) {
        const elapsed = performance.now() - animation.startTime;
        const progress = Math.min(elapsed / animation.duration, 1);

        const currentRotation = interpolateRotation(
          animation.startRotation,
          animation.targetRotation,
          progress,
        );

        updateCameraPosition(currentRotation, controls.currentDistance);
        updateNaviCube(currentRotation);
        setCameraRotation(currentRotation);

        if (progress >= 1) {
          animation.isAnimating = false;
        }
      }
      // CHANGED: Handle manual rotation inertia with continuous rotation
      // CHANGED: Handle manual rotation inertia with continuous rotation
      else if (
        !controls.isRotating &&
        (Math.abs(controls.rotationVelocity.x) > 0.01 ||
          Math.abs(controls.rotationVelocity.y) > 0.01)
      ) {
        const newRotation = {
          x: currentRotationRef.current.x + controls.rotationVelocity.x,
          y: currentRotationRef.current.y + controls.rotationVelocity.y,
        };

        // REMOVED: Don't normalize - let rotation values grow naturally
        // This prevents NaviCube from jumping

        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);

        controls.rotationVelocity.x *= controls.inertia;
        controls.rotationVelocity.y *= controls.inertia;

        if (Math.abs(controls.rotationVelocity.x) < 0.01)
          controls.rotationVelocity.x = 0;
        if (Math.abs(controls.rotationVelocity.y) < 0.01)
          controls.rotationVelocity.y = 0;
      } else if (
        !controls.isRotating &&
        !animation.isAnimating &&
        noahGeometryRef.current.length > 0 && // <--- CHANGED THIS LINE
        Date.now() - lastInteractionRef.current > IDLE_TIME_THRESHOLD
      ) {
        // Continuous slow rotation
        const newRotation = {
          x: currentRotationRef.current.x,
          y: currentRotationRef.current.y - AUTO_ROTATE_SPEED,
        };

        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);
      }

      // Check for container size changes every frame and update immediately
      if (mountRef.current && rendererRef.current) {
        const newWidth = mountRef.current.clientWidth;
        const newHeight = mountRef.current.clientHeight;

        if (
          rendererRef.current.domElement.width !== newWidth ||
          rendererRef.current.domElement.height !== newHeight
        ) {
          rendererRef.current.setSize(newWidth, newHeight);

          if (cameraRef.current) {
            cameraRef.current.aspect = newWidth / newHeight;
            cameraRef.current.updateProjectionMatrix();
          }

          if (orthoCameraRef.current) {
            const aspect = newWidth / newHeight;
            const box = new THREE.Box3();
            let hasObjects = false;

            sceneRef.current?.traverse((child) => {
              if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
                const meshBox = new THREE.Box3().setFromObject(child);
                box.union(meshBox);
                hasObjects = true;
              }
            });

            let frustumSize = 50;
            if (hasObjects) {
              const size = box.getSize(new THREE.Vector3());
              const maxDimension = Math.max(size.x, size.y, size.z);
              frustumSize = maxDimension * 2;
            }

            orthoCameraRef.current.left = (-frustumSize * aspect) / 2;
            orthoCameraRef.current.right = (frustumSize * aspect) / 2;
            orthoCameraRef.current.top = frustumSize / 2;
            orthoCameraRef.current.bottom = -frustumSize / 2;
            orthoCameraRef.current.updateProjectionMatrix();
          }
        }
      }

      const currentCamera = isOrthographicRef.current
        ? orthoCameraRef.current
        : cameraRef.current;

      if (currentCamera && rendererRef.current && sceneRef.current) {
        rendererRef.current.render(sceneRef.current, currentCamera);
      }
    };
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      clearHoverVisual();
      clearSelectedVisuals();
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();

      // ADD THESE 3 LINES:
      if (colorSaveTimeoutRef.current) {
        clearTimeout(colorSaveTimeoutRef.current);
      }
    };
  }, []);

  // Update scene background when theme changes
  // Update scene background when theme changes
  useEffect(() => {
    if (!sceneRef.current) return;

    const updateBackground = () => {
      // Use background color when tabs exist, white when empty
      const bgColor =
        modelTabs && modelTabs.length > 0
          ? getComputedStyle(document.documentElement)
              .getPropertyValue("--color-background")
              .trim()
          : getComputedStyle(document.documentElement)
              .getPropertyValue("--color-white")
              .trim();

      if (sceneRef.current) {
        sceneRef.current.background = new THREE.Color(bgColor || "#ffffff");
      }
    };

    // Update immediately
    updateBackground();

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          updateBackground();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [modelTabs]);

  // Handle camera mode transitions
  // Handle camera mode transitions with extensive debugging
  // Handle camera mode transitions - wait for geometry to be loaded
  // Handle camera mode transitions - simplified without geometry dependency
  // Handle camera mode transitions - FINAL SIMPLE VERSION

  useEffect(() => {
    // console.log("🔍 GEOMETRY EFFECT TRIGGERED", {
    //   geometryLength: noahGeometry.length,
    //   isEmpty: noahGeometry.length === 0,
    // });

    if (!sceneRef.current) return;

    // ✅ CHECK: Is this from a command execution (geometry modification)?
    const isFromCommand = noahGeometry.some((g) => g._skipCameraReset);

    if (isFromCommand) {
      // console.log(
      //   "⏭️ GEOMETRY: Skipping camera reset - this is a geometry modification"
      // );
      // Remove old meshes
      const meshesToRemove: THREE.Mesh[] = [];
      sceneRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
          meshesToRemove.push(child);
        }
      });

      meshesToRemove.forEach((mesh) => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
        sceneRef.current?.remove(mesh);
      });

      // Load new geometry WITHOUT camera changes
      noahGeometry.forEach((geomData) => {
        if (geomData.stl_data) {
          try {
            const geometry = stlLoader.loadSTLFromBase64(geomData.stl_data);
            geometry.uuid = THREE.MathUtils.generateUUID();

            const hexColor = colorValue.startsWith("#")
              ? colorValue
              : `#${colorValue}`;
            const color = new THREE.Color(hexColor);

            const material = new THREE.MeshStandardMaterial({
              color: color,
              metalness: 0.1,
              roughness: 0.2,
              transparent: transparencyValue < 100,
              opacity: transparencyValue / 100,
              side:
                transparencyValue < 100 ? THREE.DoubleSide : THREE.FrontSide,
              depthWrite: transparencyValue >= 100,
            });

            // --- START REPLACEMENT ---
            const mesh = new THREE.Mesh(geometry, material);
            mesh.uuid = THREE.MathUtils.generateUUID();

            // 1. Rotate FIRST
            mesh.rotation.x = -Math.PI / 2;

            // 2. Force update to calculate real world bounds
            mesh.updateMatrixWorld();

            // 3. Center the mesh based on the ROTATED box
            const box = new THREE.Box3().setFromObject(mesh);
            const center = new THREE.Vector3();
            box.getCenter(center);
            mesh.position.sub(center);

            mesh.userData = {
              isNoahShape: true,
              name: geomData.name,
              type: geomData.type,
            };
            // --- END REPLACEMENT ---

            mesh.castShadow = true;
            mesh.receiveShadow = true;

            sceneRef.current?.add(mesh);
          } catch (error) {
            // console.error(`Failed to load geometry: ${geomData.name}`, error);
          }
        }
      });

      // console.log("✅ GEOMETRY: Updated meshes, preserved camera position");
      return; // ← EXIT EARLY
    }

    // ✅ CHECK: Is this error recovery?
    const isErrorRecovery = noahGeometry.some((g) => g._errorRecovery);
    if (isErrorRecovery) {
      // console.log("🔄 Error recovery geometry detected - forcing update");
      isFirstGeometryLoad.current = false;
    }

    // ✅ CHECK: Is this from branching/navigation (new project load)?
    const isBranchingLoad = noahGeometry.some((g) => g._autoFit);
    if (isBranchingLoad) {
      // console.log("🔄 Branching detected - will auto-fit camera");
      isFirstGeometryLoad.current = true;
    }

    // ✅ CHECK: Forced first load flag
    const isForceFirstLoad = (window as any).__forceFirstGeometryLoad;
    if (isForceFirstLoad) {
      // console.log("🔄 Forced first geometry load detected");
      isFirstGeometryLoad.current = true;
      delete (window as any).__forceFirstGeometryLoad;
    }

    // ✅ CRITICAL FIX: Reset camera refs ONLY for truly new projects
    if (
      (isBranchingLoad || isForceFirstLoad) &&
      !(window as any).__isTabSwitch
    ) {
      // console.log("🎥 RESETTING CAMERA REFS - Brand new project detected");

      // Reset to default camera position
      currentRotationRef.current = { x: -30, y: -45 };
      cameraControlsRef.current.currentDistance = 100;

      // Stop any ongoing animation/inertia
      animationRef.current.isAnimating = false;
      cameraControlsRef.current.rotationVelocity = { x: 0, y: 0 };
      cameraControlsRef.current.isRotating = false;
    }

    // Remove existing meshes
    const meshesToRemove: THREE.Mesh[] = [];
    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
        meshesToRemove.push(child);
      }
    });

    const hadGeometry = meshesToRemove.length > 0;

    meshesToRemove.forEach((mesh) => {
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((m) => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }
      sceneRef.current?.remove(mesh);
    });

    if (meshesToRemove.length > 0) {
      // console.log(`🗑️ Removed ${meshesToRemove.length} old meshes`);
      setSelectedItems([]);
    }

    if (noahGeometry.length === 0) return;

    // console.log(`📦 Loading ${noahGeometry.length} new geometry items`);

    // Load new geometry
    const allBounds = new THREE.Box3();
    let hasGeometry = false;

    noahGeometry.forEach((geomData, index) => {
      if (geomData.stl_data) {
        try {
          const geometry = stlLoader.loadSTLFromBase64(geomData.stl_data);
          geometry.uuid = THREE.MathUtils.generateUUID();

          const hexColor = colorValue.startsWith("#")
            ? colorValue
            : `#${colorValue}`;
          const color = new THREE.Color(hexColor);

          const material = new THREE.MeshStandardMaterial({
            color: color,
            metalness: 0.1,
            roughness: 0.2,
            transparent: transparencyValue < 100,
            opacity: transparencyValue / 100,
            side: transparencyValue < 100 ? THREE.DoubleSide : THREE.FrontSide,
            depthWrite: transparencyValue >= 100,
          });

          // --- START REPLACEMENT ---
          const mesh = new THREE.Mesh(geometry, material);
          mesh.uuid = THREE.MathUtils.generateUUID();

          // 1. Rotate FIRST
          mesh.rotation.x = -Math.PI / 2;

          // 2. Force update to calculate real world bounds
          mesh.updateMatrixWorld();

          // 3. Center the mesh based on the ROTATED box
          const box = new THREE.Box3().setFromObject(mesh);
          const center = new THREE.Vector3();
          box.getCenter(center);
          mesh.position.sub(center);

          mesh.userData = {
            isNoahShape: true,
            name: geomData.name,
            type: geomData.type,
            _forceUpdate: geomData._forceUpdate,
          };
          // --- END REPLACEMENT ---

          mesh.castShadow = true;
          mesh.receiveShadow = true;

          sceneRef.current?.add(mesh);

          const meshBox = new THREE.Box3().setFromObject(mesh);
          allBounds.union(meshBox);
          hasGeometry = true;
        } catch (error) {
          // console.error(`Failed to load STL for ${geomData.name}:`, error);
        }
      }
    });

    // AUTO-FIT CAMERA ONLY ON FIRST LOAD
    const isBranchSwitch =
      lastBranchRef.current !== currentBranch && lastBranchRef.current !== "";
    const isTabSwitch = (window as any).__isTabSwitch;
    const shouldAutoFit =
      (isFirstGeometryLoad.current || isErrorRecovery || isBranchingLoad) &&
      !isTabSwitch;

    if (isTabSwitch) {
      // console.log("⏩ Skipping auto-fit - tab switch detected");
      setTimeout(() => {
        delete (window as any).__isTabSwitch;
      }, 500);
    }

    if (isBranchSwitch) {
      // console.log(
      //   `🔄 Branch switch detected: ${lastBranchRef.current} -> ${currentBranch}, forcing camera reset`
      // );
      isFirstGeometryLoad.current = true;
    }

    lastBranchRef.current = currentBranch;

    if (hasGeometry && shouldAutoFit) {
      // console.log("🎥 AUTO-FITTING camera to geometry");
      isFirstGeometryLoad.current = false;
      delete (window as any).__resetCameraForNewProject;

      const size = allBounds.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const center = allBounds.getCenter(new THREE.Vector3());

      if (isOrthographic) {
        const aspect = mountRef.current
          ? mountRef.current.clientWidth / mountRef.current.clientHeight
          : 1;
        const targetBounds = 0.5;
        const frustumSize = (maxDim / targetBounds) * 1.2;

        if (orthoCameraRef.current) {
          orthoCameraRef.current.left = (-frustumSize * aspect) / 2;
          orthoCameraRef.current.right = (frustumSize * aspect) / 2;
          orthoCameraRef.current.top = frustumSize / 2;
          orthoCameraRef.current.bottom = -frustumSize / 2;
          orthoCameraRef.current.near = -1000;
          orthoCameraRef.current.far = 1000;
          orthoCameraRef.current.updateProjectionMatrix();

          const distance = maxDim * 3;
          cameraControlsRef.current.currentDistance = distance;
          updateCameraPosition(currentRotationRef.current, distance);

          // console.log(`Ortho auto-fit: frustumSize=${frustumSize.toFixed(2)}`);
        }
      } else {
        const fov = 50;
        let distance = maxDim * 2.5;

        const camDirX = 0.7;
        const camDirY = 0.5;
        const camDirZ = 0.7;

        if (cameraRef.current) {
          cameraRef.current.position.set(
            center.x + distance * camDirX,
            center.y + distance * camDirY,
            center.z + distance * camDirZ,
          );
          cameraRef.current.lookAt(center);
          cameraRef.current.updateProjectionMatrix();
        }

        const corners = [
          new THREE.Vector3(allBounds.min.x, allBounds.min.y, allBounds.min.z),
          new THREE.Vector3(allBounds.min.x, allBounds.min.y, allBounds.max.z),
          new THREE.Vector3(allBounds.min.x, allBounds.max.y, allBounds.min.z),
          new THREE.Vector3(allBounds.min.x, allBounds.max.y, allBounds.max.z),
          new THREE.Vector3(allBounds.max.x, allBounds.min.y, allBounds.min.z),
          new THREE.Vector3(allBounds.max.x, allBounds.min.y, allBounds.max.z),
          new THREE.Vector3(allBounds.max.x, allBounds.max.y, allBounds.min.z),
          new THREE.Vector3(allBounds.max.x, allBounds.max.y, allBounds.max.z),
        ];

        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;

        corners.forEach((corner) => {
          const projected = corner.clone().project(cameraRef.current!);
          minX = Math.min(minX, projected.x);
          maxX = Math.max(maxX, projected.x);
          minY = Math.min(minY, projected.y);
          maxY = Math.max(maxY, projected.y);
        });

        const targetBounds = 0.6;
        const currentWidth = maxX - minX;
        const currentHeight = maxY - minY;
        const scaleNeeded = Math.max(
          currentWidth / (targetBounds * 2),
          currentHeight / (targetBounds * 2),
        );

        // Guard against NaN from invalid projection
        if (!isFinite(scaleNeeded) || scaleNeeded <= 0) {
          console.warn("⚠️ Invalid scaleNeeded, using fallback distance");
          distance = maxDim * 3; // Fallback to simple multiplier
        } else {
          distance = distance * scaleNeeded * 1.2;
        }
        cameraControlsRef.current.currentDistance = distance;
        updateCameraPosition(currentRotationRef.current, distance);

        // console.log(`Perspective auto-fit: distance=${distance.toFixed(2)}`);
      }

      // ✅ UPDATE NaviCube after auto-fit completes
      updateNaviCube(currentRotationRef.current);
      setCameraRotation(currentRotationRef.current);
    } else if (hasGeometry) {
      // console.log("Preserving user's camera position (geometry update)");
      updateCameraPosition(
        currentRotationRef.current,
        cameraControlsRef.current.currentDistance,
      );
    }
  }, [
    noahGeometry,
    isOrthographic,
    updateCameraPosition,
    updateNaviCube,
    currentBranch,
  ]);

  // Separate effect to handle ONLY color/transparency changes without reloading geometry
  // Separate effect to handle ONLY color/transparency changes without affecting camera
  useEffect(() => {
    if (!sceneRef.current) return;

    const hexColor = colorValue.startsWith("#") ? colorValue : `#${colorValue}`;
    const color = new THREE.Color(hexColor);

    // console.log(
    //   `🎨 Updating mesh colors to: ${hexColor}, transparency: ${transparencyValue}%`
    // );

    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
        if (child.material instanceof THREE.MeshStandardMaterial) {
          child.material.color.copy(color);
          child.material.opacity = transparencyValue / 100;
          child.material.transparent = transparencyValue < 100;
          child.material.side =
            transparencyValue < 100 ? THREE.DoubleSide : THREE.FrontSide;
          child.material.depthWrite = transparencyValue >= 100;
          child.material.needsUpdate = true;
        }
      }
    });
  }, [colorValue, transparencyValue]);

  useEffect(() => {
    if (!sceneRef.current) return;

    if (isOrthographic && orthoCameraRef.current && mountRef.current) {
      const aspect =
        mountRef.current.clientWidth / mountRef.current.clientHeight;

      const box = new THREE.Box3();
      let hasObjects = false;

      sceneRef.current!.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
          const meshBox = new THREE.Box3().setFromObject(child);
          box.union(meshBox);
          hasObjects = true;
        }
      });

      let frustumSize = 50;
      if (hasObjects) {
        const size = box.getSize(new THREE.Vector3());
        const maxDimension = Math.max(size.x, size.y, size.z);
        frustumSize = maxDimension * 2;
      }

      orthoCameraRef.current.left = (-frustumSize * aspect) / 2;
      orthoCameraRef.current.right = (frustumSize * aspect) / 2;
      orthoCameraRef.current.top = frustumSize / 2;
      orthoCameraRef.current.bottom = -frustumSize / 2;
      orthoCameraRef.current.near = -1000;
      orthoCameraRef.current.far = 1000;
      orthoCameraRef.current.zoom = 1;
      orthoCameraRef.current.updateProjectionMatrix();

      const controls = cameraControlsRef.current;
      const spherical = new THREE.Spherical();
      spherical.radius = frustumSize;
      spherical.phi = THREE.MathUtils.degToRad(
        90 + currentRotationRef.current.x,
      );
      spherical.theta = THREE.MathUtils.degToRad(-currentRotationRef.current.y);

      orthoCameraRef.current.position.setFromSpherical(spherical);
      orthoCameraRef.current.lookAt(0, 0, 0);
    } else if (!isOrthographic && cameraRef.current && mountRef.current) {
      const aspect =
        mountRef.current.clientWidth / mountRef.current.clientHeight;
      cameraRef.current.aspect = aspect;
      cameraRef.current.updateProjectionMatrix();

      const controls = cameraControlsRef.current;
      const currentDistance = controls.currentDistance;
      const spherical = new THREE.Spherical();

      spherical.radius = currentDistance;
      spherical.phi = THREE.MathUtils.degToRad(
        90 + currentRotationRef.current.x,
      );
      spherical.theta = THREE.MathUtils.degToRad(-currentRotationRef.current.y);

      cameraRef.current.position.setFromSpherical(spherical);
      cameraRef.current.lookAt(0, 0, 0);
    }
  }, [isOrthographic]);
  // Update mesh colors when colorValue changes
  // Update mesh colors when colorValue changes
  useEffect(() => {
    if (!sceneRef.current) return;
    const hexColor = colorValue.startsWith("#") ? colorValue : `#${colorValue}`;
    const color = new THREE.Color(hexColor);
    // console.log(`🎨 Updating mesh colors to: ${hexColor}`);
    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
        if (child.material instanceof THREE.MeshStandardMaterial) {
          child.material.color.copy(color);
          child.material.needsUpdate = true;
        }
      }
    });
  }, [colorValue]);

  // Apply transparency changes to meshes when transparencyValue changes
  useEffect(() => {
    if (!sceneRef.current) return;

    // FIXED: 100% = opaque (opacity 1.0), 0% = transparent (opacity 0.0)
    const opacity = transparencyValue / 100;
    // console.log(
    //   `Setting transparency: ${transparencyValue}% (opacity: ${opacity})`
    // );

    // Update all Noah meshes
    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData?.isNoahShape) {
        if (child.material instanceof THREE.MeshStandardMaterial) {
          if (transparencyValue < 100) {
            // Enable transparency
            child.material.transparent = true;
            child.material.opacity = transparencyValue / 100;
            child.material.side = THREE.DoubleSide;
            child.castShadow = true; // Keep shadows for depth
            child.material.depthWrite = false; // Important for transparency sorting
          } else {
            // Fully opaque
            child.material.transparent = false;
            child.material.opacity = 1.0;
            child.material.side = THREE.FrontSide;
            child.castShadow = true;
            child.material.depthWrite = true;
          }
          child.material.needsUpdate = true;
        }
      }
    });

    // Update lighting for transparent mode
    // Update lighting for transparent mode
    sceneRef.current.traverse((child) => {
      if (child instanceof THREE.DirectionalLight) {
        if (transparencyValue < 100) {
          // Remove this entire if block
        } else {
          // Remove this entire else block
        }
      }
    });
  }, [transparencyValue]);

  // Mouse interaction with enhanced camera controls
  // Mouse interaction with enhanced camera controls
  // Mouse interaction with enhanced camera controls
  useEffect(() => {
    if (!sceneRef.current || !rendererRef.current || !mountRef.current) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const controls = cameraControlsRef.current;

    const handleMouseDown = (event: MouseEvent) => {
      lastInteractionRef.current = Date.now(); // Stops rotation, starts 30s timer
      if (!noahGeometry || noahGeometry.length === 0) return;
      // ← REMOVE THIS CHECK - we want to allow camera rotation during branching
      // if (!noahGeometry || noahGeometry.length === 0) return;

      // ← REMOVE THIS CHECK - we want to allow camera rotation during branching
      // if (isAnyButtonLoading) {
      //   return; // Prevent interaction during branching
      // }

      controls.isRotating = true;
      controls.lastMousePosition = { x: event.clientX, y: event.clientY };
      controls.mouseDownPosition = { x: event.clientX, y: event.clientY };
      controls.rotationVelocity = { x: 0, y: 0 };
      if (mountRef.current) {
        mountRef.current.style.cursor = "grabbing";
      }
    };

    const handleMouseUp = () => {
      controls.isRotating = false;

      if (mountRef.current) {
        mountRef.current.style.cursor = "grab";
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (controls.isRotating) {
        // ← ADD THIS CHECK
        if (!noahGeometry || noahGeometry.length === 0) return;

        // ← REMOVE THIS CHECK - we want to allow zooming during branching
        // if (isAnyButtonLoading) return;

        const deltaX = event.clientX - controls.lastMousePosition.x;
        const deltaY = event.clientY - controls.lastMousePosition.y;

        const rotationSpeed = 0.5;
        const deltaRotationX = deltaY * rotationSpeed;
        const deltaRotationY = -deltaX * rotationSpeed;

        controls.rotationVelocity.x = deltaRotationX * 0.1;
        controls.rotationVelocity.y = deltaRotationY * 0.1;

        // CHANGED: Remove X-axis clamping for continuous rotation
        const newRotation = {
          x: currentRotationRef.current.x + deltaRotationX,
          y: currentRotationRef.current.y + deltaRotationY,
        };

        // REMOVED: Don't normalize - let rotation values grow naturally
        // This prevents NaviCube from jumping

        animationRef.current.isAnimating = false;
        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);
        // setCameraRotation(newRotation);

        controls.lastMousePosition = { x: event.clientX, y: event.clientY };
      } else {
        // ← ADD THIS CHECK for hover interactions too
        if (!noahGeometry || noahGeometry.length === 0) {
          if (mountRef.current) {
            mountRef.current.style.cursor = "default";
          }
          return;
        }
        if (!mountRef.current) return;

        const rect = mountRef.current.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const currentCamera = isOrthographic
          ? orthoCameraRef.current
          : cameraRef.current;
        if (currentCamera) {
          raycaster.setFromCamera(mouse, currentCamera);
        }

        // Get meshes for raycasting
        const noahMeshes = sceneRef.current!.children.filter(
          (child) => child instanceof THREE.Mesh && child.userData?.isNoahShape,
        );
        const intersects = raycaster.intersectObjects(noahMeshes);

        // ← CHECK IF IN SHARED VIEW MODE FIRST
        // ← CHECK IF ACTIVE TAB IS SHARED (not the whole view)
        if (isActiveTabShared) {
          clearHoverVisual();
          if (intersects.length > 0) {
            mountRef.current.style.cursor = "not-allowed";
          } else {
            mountRef.current.style.cursor = "grab";
          }
          return; // ← Exit early, no hover visuals for shared tabs
        }

        // ← CHECK IF COMMAND IS EXECUTING
        if (isCommandExecuting) {
          // ALWAYS clear hover visual when command is executing
          clearHoverVisual();

          // Show not-allowed cursor ONLY when over the model
          if (intersects.length > 0) {
            mountRef.current.style.cursor = "not-allowed";
          } else {
            mountRef.current.style.cursor = "grab";
          }
          return; // ← Exit early, no hover visuals during command execution
        }

        // ← CHECK IF ANY BUTTON IS LOADING (branching, navigating, etc.)
        if (isAnyButtonLoading) {
          // Clear hover visual
          clearHoverVisual();

          // Show not-allowed cursor when over the model
          if (intersects.length > 0) {
            mountRef.current.style.cursor = "not-allowed";
          } else {
            mountRef.current.style.cursor = "grab";
          }
          return; // ← Exit early, no interactions during branching
        }

        // ← NORMAL BEHAVIOR WHEN NOT EXECUTING COMMAND
        // ALWAYS clear hover visual first
        clearHoverVisual();

        if (intersects.length > 0) {
          // Check multiple intersections to find flat faces behind fillets
          let bestIntersection = intersects[0];

          // Check up to 5 closest intersections
          for (let i = 0; i < Math.min(5, intersects.length); i++) {
            const candidate = intersects[i];
            if (
              candidate.faceIndex !== undefined &&
              candidate.object instanceof THREE.Mesh
            ) {
              const mesh = candidate.object as THREE.Mesh;
              const geometry = mesh.geometry;
              const normals = geometry.attributes.normal?.array;

              if (normals) {
                const faceIdx = candidate.faceIndex;
                const normal = new THREE.Vector3(
                  normals[faceIdx! * 9],
                  normals[faceIdx! * 9 + 1],
                  normals[faceIdx! * 9 + 2],
                );

                const worldNormal = normal.clone();
                worldNormal.applyMatrix3(
                  new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld),
                );
                worldNormal.normalize();

                const absX = Math.abs(worldNormal.x);
                const absY = Math.abs(worldNormal.y);
                const absZ = Math.abs(worldNormal.z);

                const isFlat = absX > 0.95 || absY > 0.95 || absZ > 0.95;

                if (isFlat) {
                  bestIntersection = candidate;
                  break;
                }
              }
            }
          }

          const intersectedMesh = bestIntersection.object as THREE.Mesh;
          const intersectionPoint = bestIntersection.point;
          const face = bestIntersection.face;

          const edgeThreshold = 0.5;
          let closestEdgeIndex = 0;
          let minEdgeDistance = Infinity;

          const edges = new THREE.EdgesGeometry(intersectedMesh.geometry);
          const positions = edges.attributes.position.array;

          for (let i = 0; i < positions.length; i += 6) {
            const start = new THREE.Vector3(
              positions[i],
              positions[i + 1],
              positions[i + 2],
            );
            const end = new THREE.Vector3(
              positions[i + 3],
              positions[i + 4],
              positions[i + 5],
            );

            start.applyMatrix4(intersectedMesh.matrixWorld);
            end.applyMatrix4(intersectedMesh.matrixWorld);

            const line = new THREE.Line3(start, end);
            const closestPoint = new THREE.Vector3();
            line.closestPointToPoint(intersectionPoint, true, closestPoint);
            const distance = intersectionPoint.distanceTo(closestPoint);

            if (distance < minEdgeDistance) {
              minEdgeDistance = distance;
              closestEdgeIndex = i / 6;
            }
          }

          const isHoveringEdge = minEdgeDistance < edgeThreshold;

          if (isHoveringEdge) {
            const edgeGroup = groupCircularEdges(
              intersectedMesh,
              Math.floor(closestEdgeIndex),
            );
            if (edgeGroup.length > 1) {
              hoverVisualRef.current = createEdgeGroupVisual(
                intersectedMesh,
                edgeGroup,
                undefined,
                6,
              );
            } else {
              hoverVisualRef.current = createEdgeVisual(
                intersectedMesh,
                Math.floor(closestEdgeIndex),
                undefined,
                6,
              );
            }
            if (hoverVisualRef.current) {
              sceneRef.current!.add(hoverVisualRef.current);
            }
            mountRef.current!.style.cursor = "pointer";
          } else if (face) {
            const faceIndex = Math.floor(intersects[0].faceIndex || 0);

            const geometry = intersectedMesh.geometry;
            const positions = geometry.attributes.position.array;
            const normals = geometry.attributes.normal?.array;

            if (normals) {
              const clickedNormal = new THREE.Vector3(
                normals[faceIndex * 9],
                normals[faceIndex * 9 + 1],
                normals[faceIndex * 9 + 2],
              );

              const connectedTriangles = new Set<number>();
              const toProcess = [faceIndex];
              connectedTriangles.add(faceIndex);
              const tolerance = 0.0001;

              while (toProcess.length > 0) {
                const currentTriIndex = toProcess.pop()!;
                const currentStart = currentTriIndex * 9;
                const currentVertices = [
                  new THREE.Vector3(
                    positions[currentStart],
                    positions[currentStart + 1],
                    positions[currentStart + 2],
                  ),
                  new THREE.Vector3(
                    positions[currentStart + 3],
                    positions[currentStart + 4],
                    positions[currentStart + 5],
                  ),
                  new THREE.Vector3(
                    positions[currentStart + 6],
                    positions[currentStart + 7],
                    positions[currentStart + 8],
                  ),
                ];

                for (let i = 0; i < positions.length; i += 9) {
                  const triIndex = i / 9;
                  if (connectedTriangles.has(triIndex)) continue;

                  const triangleNormal = new THREE.Vector3(
                    normals[i],
                    normals[i + 1],
                    normals[i + 2],
                  );
                  if (clickedNormal.distanceTo(triangleNormal) >= tolerance)
                    continue;

                  const otherVertices = [
                    new THREE.Vector3(
                      positions[i],
                      positions[i + 1],
                      positions[i + 2],
                    ),
                    new THREE.Vector3(
                      positions[i + 3],
                      positions[i + 4],
                      positions[i + 5],
                    ),
                    new THREE.Vector3(
                      positions[i + 6],
                      positions[i + 7],
                      positions[i + 8],
                    ),
                  ];

                  let sharedVertices = 0;
                  for (const currentVertex of currentVertices) {
                    for (const otherVertex of otherVertices) {
                      if (currentVertex.distanceTo(otherVertex) < 0.001) {
                        sharedVertices++;
                        break;
                      }
                    }
                  }

                  if (sharedVertices >= 2) {
                    connectedTriangles.add(triIndex);
                    toProcess.push(triIndex);
                  }
                }
              }

              const canonicalFaceIndex = Math.min(
                ...Array.from(connectedTriangles),
              );
              hoverVisualRef.current = createFaceVisual(
                intersectedMesh,
                canonicalFaceIndex,
                undefined,
                0.3,
              );
            } else {
              hoverVisualRef.current = createFaceVisual(
                intersectedMesh,
                faceIndex,
                undefined,
                0.3,
              );
            }

            if (hoverVisualRef.current) {
              sceneRef.current!.add(hoverVisualRef.current);
            }
            mountRef.current!.style.cursor = "pointer";
          }
        } else {
          mountRef.current!.style.cursor = "grab";
        }
      }
    };

    const handleClick = (event: MouseEvent) => {
      // ← ADD THIS CHECK
      if (!noahGeometry || noahGeometry.length === 0) return;

      // ← ADD THIS NEW CHECK for shared view
      // ← CHECK IF ACTIVE TAB IS SHARED (not the whole view)
      if (isActiveTabShared) {
        return; // Prevent clicks on shared tabs only
      }

      // ← ADD THIS NEW CHECK
      if (isAnyButtonLoading) {
        return; // Prevent clicks during branching/navigation
      }

      if (isCommandExecuting) {
        return;
      }

      // Calculate how far mouse moved since mousedown
      const deltaX = Math.abs(event.clientX - controls.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - controls.mouseDownPosition.y);
      const totalMovement = deltaX + deltaY;

      // Prevent selection if user was rotating OR if mouse moved more than 5 pixels
      if (
        Math.abs(controls.rotationVelocity.x) > 0.1 ||
        Math.abs(controls.rotationVelocity.y) > 0.1 ||
        controls.isRotating ||
        totalMovement > 5
      ) {
        return;
      }

      if (!mountRef.current) return;

      const rect = mountRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const currentCamera = isOrthographic
        ? orthoCameraRef.current
        : cameraRef.current;
      if (currentCamera) {
        raycaster.setFromCamera(mouse, currentCamera);
      }

      const noahMeshes = sceneRef.current!.children.filter(
        (child) => child instanceof THREE.Mesh && child.userData?.isNoahShape,
      );

      const intersects = raycaster.intersectObjects(noahMeshes);

      if (intersects.length > 0) {
        const intersectedMesh = intersects[0].object as THREE.Mesh;
        const intersectionPoint = intersects[0].point;

        const edgeThreshold = 0.5;
        let closestEdgeIndex = 0;
        let minEdgeDistance = Infinity;

        const edges = new THREE.EdgesGeometry(intersectedMesh.geometry);
        const positions = edges.attributes.position.array;

        for (let i = 0; i < positions.length; i += 6) {
          const start = new THREE.Vector3(
            positions[i],
            positions[i + 1],
            positions[i + 2],
          );
          const end = new THREE.Vector3(
            positions[i + 3],
            positions[i + 4],
            positions[i + 5],
          );

          start.applyMatrix4(intersectedMesh.matrixWorld);
          end.applyMatrix4(intersectedMesh.matrixWorld);

          const line = new THREE.Line3(start, end);
          const closestPoint = new THREE.Vector3();
          line.closestPointToPoint(intersectionPoint, true, closestPoint);
          const distance = intersectionPoint.distanceTo(closestPoint);

          if (distance < minEdgeDistance) {
            minEdgeDistance = distance;
            closestEdgeIndex = i / 6;
          }
        }

        const isEdgeSelection = minEdgeDistance < edgeThreshold;

        if (isEdgeSelection) {
          // EDGE SELECTION
          const edgeGroup = groupCircularEdges(
            intersectedMesh,
            Math.floor(closestEdgeIndex),
          );

          let measurement = 0;

          if (edgeGroup.length > 1) {
            const radius = calculateCircleRadius(intersectedMesh, edgeGroup);
            measurement = radius * 2;
          } else {
            measurement = calculateEdgeLength(
              intersectedMesh,
              Math.floor(closestEdgeIndex),
            );
          }

          const actualIndex = Math.floor(closestEdgeIndex);
          let newSelection: SelectionItem = {
            type: "edge",
            objectName: intersectedMesh.userData.name || "Unknown",
            index: actualIndex,
            mesh: intersectedMesh,
            faceIndex: 0,
            edgeGroup: edgeGroup,
          };

          if (edgeGroup.length > 1) {
            const groupCoordinates: number[] = [];
            edgeGroup.forEach((edgeIdx) => {
              const start = new THREE.Vector3(
                positions[edgeIdx * 6],
                positions[edgeIdx * 6 + 1],
                positions[edgeIdx * 6 + 2],
              );
              const end = new THREE.Vector3(
                positions[edgeIdx * 6 + 3],
                positions[edgeIdx * 6 + 4],
                positions[edgeIdx * 6 + 5],
              );
              groupCoordinates.push(
                start.x,
                start.y,
                start.z,
                end.x,
                end.y,
                end.z,
              );
            });
            newSelection.browserCoordinates = groupCoordinates;
          } else {
            const start = new THREE.Vector3(
              positions[actualIndex * 6],
              positions[actualIndex * 6 + 1],
              positions[actualIndex * 6 + 2],
            );
            const end = new THREE.Vector3(
              positions[actualIndex * 6 + 3],
              positions[actualIndex * 6 + 4],
              positions[actualIndex * 6 + 5],
            );
            newSelection.browserCoordinates = [
              start.x,
              start.y,
              start.z,
              end.x,
              end.y,
              end.z,
            ];
          }

          setSelectedItems((prev) => {
            const exists = prev.find(
              (item) =>
                item.objectName === newSelection.objectName &&
                item.type === newSelection.type &&
                item.index === newSelection.index,
            );

            if (exists) {
              // ✅ FIX: Deselecting - only reset measurement if NO other edges remain
              const remainingEdges = prev.filter(
                (item) => item !== exists && item.type === "edge",
              );

              if (remainingEdges.length === 0) {
                // No edges left - reset measurement
                setMeasureDistance(0);
                if (onMeasurementChange) {
                  onMeasurementChange(0);
                }
              } else {
                // Other edges still selected - recalculate measurement for the last remaining edge
                const lastEdge = remainingEdges[remainingEdges.length - 1];
                let recalculatedMeasurement = 0;

                if (lastEdge.edgeGroup && lastEdge.edgeGroup.length > 1) {
                  // It's a circular edge group
                  const radius = calculateCircleRadius(
                    lastEdge.mesh,
                    lastEdge.edgeGroup,
                  );
                  recalculatedMeasurement = radius * 2;
                } else {
                  // Single edge
                  recalculatedMeasurement = calculateEdgeLength(
                    lastEdge.mesh,
                    lastEdge.index,
                  );
                }

                setMeasureDistance(recalculatedMeasurement);
                if (onMeasurementChange) {
                  onMeasurementChange(recalculatedMeasurement);
                }
              }

              return prev.filter((item) => item !== exists);
            } else {
              // ✅ FIX: Selecting - set measurement AND notify parent
              setMeasureDistance(measurement);
              if (onMeasurementChange) {
                onMeasurementChange(measurement);
              }
              return [...prev, newSelection];
            }
          });
        } else if (intersects[0].face) {
          // FACE SELECTION
          const faceIndex = Math.floor(intersects[0].faceIndex || 0);

          const geometry = intersectedMesh.geometry;
          const positions = geometry.attributes.position.array;
          const normals = geometry.attributes.normal?.array;

          if (normals) {
            const clickedNormal = new THREE.Vector3(
              normals[faceIndex * 9],
              normals[faceIndex * 9 + 1],
              normals[faceIndex * 9 + 2],
            );

            const connectedTriangles = new Set<number>();
            const toProcess = [faceIndex];
            connectedTriangles.add(faceIndex);
            const tolerance = 0.0001;

            while (toProcess.length > 0) {
              const currentTriIndex = toProcess.pop()!;
              const currentStart = currentTriIndex * 9;
              const currentVertices = [
                new THREE.Vector3(
                  positions[currentStart],
                  positions[currentStart + 1],
                  positions[currentStart + 2],
                ),
                new THREE.Vector3(
                  positions[currentStart + 3],
                  positions[currentStart + 4],
                  positions[currentStart + 5],
                ),
                new THREE.Vector3(
                  positions[currentStart + 6],
                  positions[currentStart + 7],
                  positions[currentStart + 8],
                ),
              ];

              for (let i = 0; i < positions.length; i += 9) {
                const triIndex = i / 9;
                if (connectedTriangles.has(triIndex)) continue;

                const triangleNormal = new THREE.Vector3(
                  normals[i],
                  normals[i + 1],
                  normals[i + 2],
                );
                if (clickedNormal.distanceTo(triangleNormal) >= tolerance)
                  continue;

                const otherVertices = [
                  new THREE.Vector3(
                    positions[i],
                    positions[i + 1],
                    positions[i + 2],
                  ),
                  new THREE.Vector3(
                    positions[i + 3],
                    positions[i + 4],
                    positions[i + 5],
                  ),
                  new THREE.Vector3(
                    positions[i + 6],
                    positions[i + 7],
                    positions[i + 8],
                  ),
                ];

                let sharedVertices = 0;
                for (const currentVertex of currentVertices) {
                  for (const otherVertex of otherVertices) {
                    if (currentVertex.distanceTo(otherVertex) < 0.001) {
                      sharedVertices++;
                      break;
                    }
                  }
                }

                if (sharedVertices >= 2) {
                  connectedTriangles.add(triIndex);
                  toProcess.push(triIndex);
                }
              }
            }

            const canonicalFaceIndex = Math.min(
              ...Array.from(connectedTriangles),
            );

            const faceData = calculateFaceData(
              intersectedMesh,
              canonicalFaceIndex,
            );

            let newSelection: SelectionItem = {
              type: "face",
              objectName: intersectedMesh.userData.name || "Unknown",
              index: canonicalFaceIndex,
              mesh: intersectedMesh,
              faceIndex: canonicalFaceIndex,
              browserFaceData: faceData,
            };

            setSelectedItems((prev) => {
              const exists = prev.find(
                (item) =>
                  item.objectName === newSelection.objectName &&
                  item.type === newSelection.type &&
                  item.index === newSelection.index,
              );

              if (exists) {
                // ✅ FIX: Deselecting face - only reset measurement if NO edges remain
                const remainingEdges = prev.filter(
                  (item) => item !== exists && item.type === "edge",
                );

                if (remainingEdges.length === 0) {
                  // No edges left - reset measurement
                  setMeasureDistance(0);
                  if (onMeasurementChange) {
                    onMeasurementChange(0);
                  }
                } else {
                  // Edges still selected - recalculate measurement for the last remaining edge
                  const lastEdge = remainingEdges[remainingEdges.length - 1];
                  let recalculatedMeasurement = 0;

                  if (lastEdge.edgeGroup && lastEdge.edgeGroup.length > 1) {
                    // It's a circular edge group
                    const radius = calculateCircleRadius(
                      lastEdge.mesh,
                      lastEdge.edgeGroup,
                    );
                    recalculatedMeasurement = radius * 2;
                  } else {
                    // Single edge
                    recalculatedMeasurement = calculateEdgeLength(
                      lastEdge.mesh,
                      lastEdge.index,
                    );
                  }

                  setMeasureDistance(recalculatedMeasurement);
                  if (onMeasurementChange) {
                    onMeasurementChange(recalculatedMeasurement);
                  }
                }

                return prev.filter((item) => item !== exists);
              } else {
                return [...prev, newSelection];
              }
            });
          }
        }
      }
    };
    const handleWheel = (event: WheelEvent) => {
      lastInteractionRef.current = Date.now(); // Stops rotation, starts 30s timer
      if (!noahGeometry || noahGeometry.length === 0) return;
      // ← KEEP THIS CHECK (prevents zoom when no geometry)
      if (!noahGeometry || noahGeometry.length === 0) return;

      // ← REMOVE THIS CHECK - we want to allow zooming during branching
      // if (isAnyButtonLoading) return;

      event.preventDefault();

      const controls = cameraControlsRef.current;

      // Apple-style exponential zoom - instant and responsive
      const zoomSpeed = 0.002;

      const delta = event.deltaY;

      if (isOrthographicRef.current && orthoCameraRef.current) {
        // Orthographic zoom - instant exponential frustum adjustment
        const currentFrustumHeight =
          orthoCameraRef.current.top - orthoCameraRef.current.bottom;

        // Exponential zoom factor - multiplies by scroll amount for smoothness
        const zoomFactor = Math.exp(delta * zoomSpeed);
        const newFrustumHeight = currentFrustumHeight * zoomFactor;

        const aspect = mountRef.current
          ? mountRef.current.clientWidth / mountRef.current.clientHeight
          : 1;

        // Apply instantly - no interpolation
        orthoCameraRef.current.left = (-newFrustumHeight * aspect) / 2;
        orthoCameraRef.current.right = (newFrustumHeight * aspect) / 2;
        orthoCameraRef.current.top = newFrustumHeight / 2;
        orthoCameraRef.current.bottom = -newFrustumHeight / 2;
        orthoCameraRef.current.updateProjectionMatrix();
      } else {
        // Perspective zoom - instant exponential distance adjustment
        const currentDistance = controls.currentDistance;

        // Exponential zoom factor - smooth and distance-adaptive
        const zoomFactor = Math.exp(delta * zoomSpeed);
        const newDistance = Math.max(
          controls.minDistance,
          Math.min(controls.maxDistance, currentDistance * zoomFactor),
        );

        // Apply instantly - no interpolation
        controls.currentDistance = newDistance;
        updateCameraPosition(currentRotationRef.current, newDistance);
      }
    };

    const container = mountRef.current;
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("click", handleClick);
    container.addEventListener("wheel", handleWheel);

    window.addEventListener("mouseup", handleMouseUp);

    // Add touch event listeners
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    return () => {
      container?.removeEventListener("mousedown", handleMouseDown);
      container?.removeEventListener("mousemove", handleMouseMove);
      container?.removeEventListener("click", handleClick);
      container?.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mouseup", handleMouseUp);

      // Clean up touch listeners
      container?.removeEventListener("touchstart", handleTouchStart);
      container?.removeEventListener("touchmove", handleTouchMove);
      container?.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    createEdgeVisual,
    createFaceVisual,
    calculateFaceData,
    clearHoverVisual,
    updateCameraPosition,
    updateNaviCube,
    animateCameraToRotation,
    interpolateRotation,
    normalizeAngle,
    calculateShortestPath,
    isOrthographic,
    groupCircularEdges,
    createEdgeGroupVisual,
    snapToEdgeVertex,
    drawMeasurementPoint,
    drawMeasurementLine,
    isCommandExecuting,
    noahGeometry,
    isAnyButtonLoading,
    isActiveTabShared, // ← CHANGE FROM isSharedView to isActiveTabShared
  ]);

  // Touch interaction handlers
  const touchStateRef = useRef({
    isTouching: false,
    lastTouchPosition: { x: 0, y: 0 },
    touchStartPosition: { x: 0, y: 0 },
    isPinching: false,
    lastPinchDistance: 0,
    hasMoved: false, // ← NEW: Track if touch moved
    touchStartTime: 0, // ← NEW: Track when touch started
  });

  const handleTouchStart = (event: TouchEvent) => {
    lastInteractionRef.current = Date.now(); // Stops rotation, starts 30s timer
    if (!noahGeometry || noahGeometry.length === 0) return;
    // ← ADD THIS CHECK
    if (!noahGeometry || noahGeometry.length === 0) return;

    const controls = cameraControlsRef.current;
    if (event.touches.length === 1) {
      // Single finger - could be tap or rotation
      touchStateRef.current.isTouching = true;
      touchStateRef.current.lastTouchPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      touchStateRef.current.touchStartPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      touchStateRef.current.hasMoved = false; // ← Reset movement flag
      touchStateRef.current.touchStartTime = Date.now(); // ← Record start time
      controls.rotationVelocity = { x: 0, y: 0 };

      // DON'T prevent default yet - let it become a click if it's a tap
    } else if (event.touches.length === 2) {
      // Two fingers - pinch to zoom
      event.preventDefault(); // ← Prevent default for pinch
      touchStateRef.current.isPinching = true;
      touchStateRef.current.isTouching = false;

      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      touchStateRef.current.lastPinchDistance = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    // ← ADD THIS CHECK
    if (!noahGeometry || noahGeometry.length === 0) return;

    const controls = cameraControlsRef.current;
    if (touchStateRef.current.isTouching && event.touches.length === 1) {
      const deltaX =
        event.touches[0].clientX - touchStateRef.current.lastTouchPosition.x;
      const deltaY =
        event.touches[0].clientY - touchStateRef.current.lastTouchPosition.y;

      // Check if this is a significant movement (not just finger jitter)
      const totalMovement = Math.abs(deltaX) + Math.abs(deltaY);

      if (totalMovement > 5) {
        // This is a drag, not a tap
        event.preventDefault(); // ← NOW prevent default (stops click event)
        touchStateRef.current.hasMoved = true;

        const rotationSpeed = 0.5;
        const deltaRotationX = deltaY * rotationSpeed;
        const deltaRotationY = -deltaX * rotationSpeed;

        controls.rotationVelocity.x = deltaRotationX * 0.1;
        controls.rotationVelocity.y = deltaRotationY * 0.1;

        // CHANGED: Remove X-axis clamping for continuous rotation
        const newRotation = {
          x: currentRotationRef.current.x + deltaRotationX,
          y: currentRotationRef.current.y + deltaRotationY,
        };

        // REMOVED: Don't normalize - let rotation values grow naturally
        // This prevents NaviCube from jumping

        animationRef.current.isAnimating = false;
        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);
        // setCameraRotation(newRotation);

        touchStateRef.current.lastTouchPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      }
    } else if (touchStateRef.current.isPinching && event.touches.length === 2) {
      event.preventDefault(); // ← Keep preventing for pinch

      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      const currentDistance = Math.sqrt(dx * dx + dy * dy);

      const delta = currentDistance - touchStateRef.current.lastPinchDistance;
      const zoomSpeed = 0.01;

      if (isOrthographicRef.current && orthoCameraRef.current) {
        const currentFrustumHeight =
          orthoCameraRef.current.top - orthoCameraRef.current.bottom;
        const newFrustumHeight = currentFrustumHeight * (1 - delta * zoomSpeed);

        const aspect = mountRef.current
          ? mountRef.current.clientWidth / mountRef.current.clientHeight
          : 1;
        orthoCameraRef.current.left = (-newFrustumHeight * aspect) / 2;
        orthoCameraRef.current.right = (newFrustumHeight * aspect) / 2;
        orthoCameraRef.current.top = newFrustumHeight / 2;
        orthoCameraRef.current.bottom = -newFrustumHeight / 2;
        orthoCameraRef.current.updateProjectionMatrix();
      } else {
        const newDistance = Math.max(
          controls.minDistance,
          Math.min(
            controls.maxDistance,
            controls.currentDistance -
              delta * zoomSpeed * controls.currentDistance,
          ),
        );

        controls.currentDistance = newDistance;
        updateCameraPosition(currentRotationRef.current, newDistance);
      }

      touchStateRef.current.lastPinchDistance = currentDistance;
    }
  };

  const handleTouchEnd = (event: TouchEvent) => {
    // ← ADD THIS CHECK at the start, but let it clean up state
    if (!noahGeometry || noahGeometry.length === 0) {
      touchStateRef.current.isTouching = false;
      touchStateRef.current.isPinching = false;
      touchStateRef.current.hasMoved = false;
      return;
    }
    const touchDuration = Date.now() - touchStateRef.current.touchStartTime;
    const wasTap = !touchStateRef.current.hasMoved && touchDuration < 300; // ← Quick tap under 300ms

    if (event.touches.length === 0) {
      // If it was a tap (not moved, quick), let the click event fire naturally
      if (!wasTap && touchStateRef.current.hasMoved) {
        // Was a drag - we prevented default in touchmove, so click won't fire (good)
      }

      touchStateRef.current.isTouching = false;
      touchStateRef.current.isPinching = false;
      touchStateRef.current.hasMoved = false;
    } else if (event.touches.length === 1 && touchStateRef.current.isPinching) {
      // Went from 2 fingers to 1 - switch to rotation
      touchStateRef.current.isPinching = false;
      touchStateRef.current.isTouching = true;
      touchStateRef.current.lastTouchPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
      touchStateRef.current.hasMoved = false;
      touchStateRef.current.touchStartTime = Date.now();
    }
  };

  // NEW EFFECT: Restore selection after returning from Stripe
  // NEW EFFECT: Restore selection after returning from Stripe
  // NEW EFFECT: Restore selection after returning from Stripe
  useEffect(() => {
    const restoreContext = (window as any).__restoreProjectContext;
    const pendingCommandStr = sessionStorage.getItem("pendingCommand");

    // Check if we need to restore selection from EITHER source
    let selectionToRestore: any[] | null = null;
    let sourceType = "";

    if (restoreContext?.selection) {
      selectionToRestore = restoreContext.selection;
      sourceType = "restoreContext";
      // console.log("🎨 View: Found selection in restoreContext");
    } else if (pendingCommandStr) {
      try {
        const pendingCommand = JSON.parse(pendingCommandStr);
        if (pendingCommand.browserSelectionData) {
          // Convert browserSelectionData back to selection format
          selectionToRestore = [];

          if (pendingCommand.browserSelectionData.edges) {
            pendingCommand.browserSelectionData.edges.forEach((edge: any) => {
              selectionToRestore!.push({
                type: "edge",
                object: edge.object,
                index: edge.index,
                browserCoordinates: edge.coordinates,
              });
            });
          }

          if (pendingCommand.browserSelectionData.faces) {
            pendingCommand.browserSelectionData.faces.forEach((face: any) => {
              selectionToRestore!.push({
                type: "face",
                object: face.object,
                index: face.index,
                browserFaceData: {
                  center: face.center,
                  normal: face.normal,
                  area: face.area,
                },
              });
            });
          }

          sourceType = "pendingCommand";
          // console.log("🎨 View: Found selection in pendingCommand");
        }
      } catch (error) {
        // console.error("Failed to parse pending command for selection:", error);
      }
    }

    if (
      selectionToRestore &&
      selectionToRestore.length > 0 &&
      sceneRef.current
    ) {
      // console.log(
      //   `🎨 View: Restoring visual selection from ${sourceType}`,
      //   selectionToRestore
      // );

      // Wait for geometry to be loaded
      const checkAndRestore = () => {
        const noahMeshes = sceneRef.current!.children.filter(
          (child) => child instanceof THREE.Mesh && child.userData?.isNoahShape,
        ) as THREE.Mesh[];

        if (noahMeshes.length === 0) {
          // console.log("⏳ View: Waiting for geometry to load...");
          setTimeout(checkAndRestore, 500);
          return;
        }

        // console.log("✅ View: Geometry loaded, restoring selection");

        // Convert restored selection to SelectionItem format
        const restoredItems: SelectionItem[] = [];

        selectionToRestore!.forEach((item: any) => {
          // Find the mesh by object name
          const mesh = noahMeshes.find((m) => m.userData.name === item.object);

          if (!mesh) {
            // console.warn(`⚠️ View: Could not find mesh for ${item.object}`);
            return;
          }

          const selectionItem: SelectionItem = {
            type: item.type,
            objectName: item.object,
            index: item.index,
            mesh: mesh,
            faceIndex: 0,
            browserCoordinates: item.browserCoordinates,
            browserFaceData: item.browserFaceData,
            edgeGroup: item.edgeGroup,
          };

          restoredItems.push(selectionItem);
          // console.log(
          //   `✅ View: Restored ${item.type} ${item.index} on ${item.object}`
          // );
        });

        setSelectedItems(restoredItems);

        // Signal that selection restoration is complete
        setTimeout(() => {
          (window as any).__selectionRestored = true;
          // console.log(
          //   `✅ View: Selection restoration complete (from ${sourceType}), signaling RightSidebar`
          // );
        }, 100);

        // Note: Don't clear __restoreProjectContext here - RightSidebar will do it
      };

      checkAndRestore();
    }
  }, [noahGeometry]);
  // Update visuals when selection changes
  useEffect(() => {
    updateSelectionVisuals();
  }, [selectedItems, updateSelectionVisuals]);

  // Pass selection to parent
  // Pass selection to parent
  // Pass selection to parent
  useEffect(() => {
    if (onShapeSelect) {
      const selectionData = selectedItems.map((item) => ({
        type: item.type,
        object: item.objectName,
        index: item.index,
        browserCoordinates: item.browserCoordinates,
        browserFaceData: item.browserFaceData,
        edgeGroup: item.edgeGroup, // Include edge group info
      }));

      const selectionString = JSON.stringify(selectionData);
      // console.log("🔄 Sending selection to parent:", selectionData);
      onShapeSelect(selectionString);
    }
  }, [selectedItems]);

  // Add this effect for handling clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target as Node)
      ) {
        setIsExportDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add this new separate effect for color dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorDropdownRef.current &&
        !colorDropdownRef.current.contains(event.target as Node)
      ) {
        setIsColorDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Around line 670, add useEffect for closing dropdown:
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        unitDropdownRef.current &&
        !unitDropdownRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Add this new function
  const handleColorInputKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      setIsColorDropdownOpen(false);
      colorInputRef.current?.blur();
    }
  };

  const handleToggleClick = () => {
    setIsToggleOn(!isToggleOn);
    setIsOrthographic(!isOrthographic);
  };

  // NaviCube position calculation
  // NaviCube position calculation
  const getNaviCubePosition = () => {
    const baseRight = isRightSidebarOpen ? "2rem" : "2rem";
    return {
      bottom: "2rem", // Changed from top to bottom
      right: baseRight,
    };
  };

  const handleFaceClick = (face: string, region: string) => {
    // console.log(`NaviCube Click: ${face}-${region}`);
    lastInteractionRef.current = Date.now();
    const rotations = {
      // Face centers
      "front-center": { x: 0, y: 0 },
      "back-center": { x: 0, y: 180 },
      "right-center": { x: 0, y: -90 },
      "left-center": { x: 0, y: 90 },
      "top-center": { x: -90, y: 0 },
      "bottom-center": { x: 90, y: 0 },

      // Front face zones
      "front-top-left": { x: -30, y: 45 },
      "front-top": { x: -45, y: 0 },
      "front-top-right": { x: -30, y: -45 },
      "front-left": { x: 0, y: 45 },
      "front-right": { x: 0, y: -45 },
      "front-bottom-left": { x: 30, y: 45 },
      "front-bottom": { x: 45, y: 0 },
      "front-bottom-right": { x: 30, y: -45 },

      // Right face zones
      "right-top-left": { x: -30, y: -45 },
      "right-top": { x: -45, y: -90 },
      "right-top-right": { x: -30, y: -135 },
      "right-left": { x: 0, y: -45 },
      "right-right": { x: 0, y: -135 },
      "right-bottom-left": { x: 30, y: -45 },
      "right-bottom": { x: 45, y: -90 },
      "right-bottom-right": { x: 30, y: -135 },

      // Left face zones
      "left-top-left": { x: -30, y: 135 },
      "left-top": { x: -45, y: 90 },
      "left-top-right": { x: -30, y: 45 },
      "left-left": { x: 0, y: 135 },
      "left-right": { x: 0, y: 45 },
      "left-bottom-left": { x: 30, y: 135 },
      "left-bottom": { x: 45, y: 90 },
      "left-bottom-right": { x: 30, y: 45 },

      // Back face zones
      "back-top-left": { x: -30, y: -135 },
      "back-top": { x: -45, y: 180 },
      "back-top-right": { x: -30, y: 135 },
      "back-left": { x: 0, y: -135 },
      "back-right": { x: 0, y: 135 },
      "back-bottom-left": { x: 30, y: -135 },
      "back-bottom": { x: 45, y: 180 },
      "back-bottom-right": { x: 30, y: 135 },

      // Top face zones
      "top-top-left": { x: -30, y: 135 },
      "top-top": { x: -45, y: 180 },
      "top-top-right": { x: -30, y: -135 },
      "top-left": { x: -45, y: 90 },
      "top-right": { x: -45, y: -90 },
      "top-bottom-left": { x: -30, y: 45 },
      "top-bottom": { x: -45, y: 0 },
      "top-bottom-right": { x: -30, y: -45 },

      // Bottom face zones
      "bottom-top-left": { x: 30, y: 45 },
      "bottom-top": { x: 45, y: 0 },
      "bottom-top-right": { x: 30, y: -45 },
      "bottom-left": { x: 45, y: 90 },
      "bottom-right": { x: 45, y: -90 },
      "bottom-bottom-left": { x: 30, y: 135 },
      "bottom-bottom": { x: 45, y: 180 },
      "bottom-bottom-right": { x: 30, y: -135 },
    };

    const key = `${face}-${region}`;
    if (rotations[key as keyof typeof rotations]) {
      const targetRotation = rotations[key as keyof typeof rotations];

      // Normalize current rotation for comparison
      const normalizedCurrent = {
        x: currentRotationRef.current.x, // Use ref for immediate value
        y: normalizeAngle(currentRotationRef.current.y),
      };

      // Calculate shortest path to target
      const shortestTarget = calculateShortestPath(
        normalizedCurrent,
        targetRotation,
      );

      // console.log(
      //   `🎯 NaviCube: ${face}-${region} -> From (${normalizedCurrent.x}, ${normalizedCurrent.y}) to (${shortestTarget.x}, ${shortestTarget.y})`
      // );

      // Start smooth animation to target
      animateCameraToRotation(shortestTarget);
    }
  };

  const getCornerHoverElements = (corner: string) => {
    const cornerMappings = {
      "front-top-left": ["front-top-left", "left-top-right", "top-bottom-left"],
      "front-top-right": [
        "front-top-right",
        "right-top-left",
        "top-bottom-right",
      ],
      "front-bottom-left": [
        "front-bottom-left",
        "left-bottom-right",
        "bottom-top-left",
      ],
      "front-bottom-right": [
        "front-bottom-right",
        "right-bottom-left",
        "bottom-top-right",
      ],
      "right-top-left": [
        "right-top-left",
        "front-top-right",
        "top-bottom-right",
      ],
      "right-top-right": ["right-top-right", "back-top-left", "top-top-right"],
      "right-bottom-left": [
        "right-bottom-left",
        "front-bottom-right",
        "bottom-top-right",
      ],
      "right-bottom-right": [
        "right-bottom-right",
        "back-bottom-left",
        "bottom-bottom-right",
      ],
      "left-top-left": ["left-top-left", "back-top-right", "top-top-left"],
      "left-top-right": ["left-top-right", "front-top-left", "top-bottom-left"],
      "left-bottom-left": [
        "left-bottom-left",
        "back-bottom-right",
        "bottom-bottom-left",
      ],
      "left-bottom-right": [
        "left-bottom-right",
        "front-bottom-left",
        "bottom-top-left",
      ],
      "back-top-left": ["back-top-left", "right-top-right", "top-top-right"],
      "back-top-right": ["back-top-right", "left-top-left", "top-top-left"],
      "back-bottom-left": [
        "back-bottom-left",
        "right-bottom-right",
        "bottom-bottom-right",
      ],
      "back-bottom-right": [
        "back-bottom-right",
        "left-bottom-left",
        "bottom-bottom-left",
      ],
      "top-top-left": ["top-top-left", "back-top-right", "left-top-left"],
      "top-top-right": ["top-top-right", "back-top-left", "right-top-right"],
      "top-bottom-left": [
        "top-bottom-left",
        "front-top-left",
        "left-top-right",
      ],
      "top-bottom-right": [
        "top-bottom-right",
        "front-top-right",
        "right-top-left",
      ],
      "bottom-top-left": [
        "bottom-top-left",
        "front-bottom-left",
        "left-bottom-right",
      ],
      "bottom-top-right": [
        "bottom-top-right",
        "front-bottom-right",
        "right-bottom-left",
      ],
      "bottom-bottom-left": [
        "bottom-bottom-left",
        "back-bottom-right",
        "left-bottom-left",
      ],
      "bottom-bottom-right": [
        "bottom-bottom-right",
        "back-bottom-left",
        "right-bottom-right",
      ],
    };
    return cornerMappings[corner as keyof typeof cornerMappings] || [];
  };

  const getEdgeHoverElements = (edge: string) => {
    const edgeMappings = {
      "front-top": ["front-top", "top-bottom"],
      "front-bottom": ["front-bottom", "bottom-top"],
      "front-left": ["front-left", "left-right"],
      "front-right": ["front-right", "right-left"],
      "right-top": ["right-top", "top-right"],
      "right-bottom": ["right-bottom", "bottom-right"],
      "right-left": ["right-left", "front-right"],
      "right-right": ["right-right", "back-left"],
      "left-top": ["left-top", "top-left"],
      "left-bottom": ["left-bottom", "bottom-left"],
      "left-left": ["left-left", "back-right"],
      "left-right": ["left-right", "front-left"],
      "back-top": ["back-top", "top-top"],
      "back-bottom": ["back-bottom", "bottom-bottom"],
      "back-left": ["back-left", "right-right"],
      "back-right": ["back-right", "left-left"],
      "top-top": ["top-top", "back-top"],
      "top-bottom": ["top-bottom", "front-top"],
      "top-left": ["top-left", "left-top"],
      "top-right": ["top-right", "right-top"],
      "bottom-top": ["bottom-top", "front-bottom"],
      "bottom-bottom": ["bottom-bottom", "back-bottom"],
      "bottom-left": ["bottom-left", "left-bottom"],
      "bottom-right": ["bottom-right", "right-bottom"],
    };
    return edgeMappings[edge as keyof typeof edgeMappings] || [];
  };

  const handleHover = (face: string, region: string, isEntering: boolean) => {
    const elementKey = `${face}-${region}`;
    let elementsToHighlight: string[] = [];

    if (region === "center") {
      elementsToHighlight = [
        `${face}-center`,
        `${face}-top-left`,
        `${face}-top`,
        `${face}-top-right`,
        `${face}-left`,
        `${face}-right`,
        `${face}-bottom-left`,
        `${face}-bottom`,
        `${face}-bottom-right`,
      ];
    } else if (region.includes("-")) {
      elementsToHighlight = getCornerHoverElements(elementKey);
    } else {
      elementsToHighlight = getEdgeHoverElements(elementKey);
    }

    if (isEntering) {
      setHoveredElements(elementsToHighlight);
    } else {
      setHoveredElements([]);
    }
  };

  const handleExportFormatSelect = (format: string) => {
    setSelectedExportFormat(format);
    setIsExportDropdownOpen(false);
  };

  const toggleExportDropdown = () => {
    setIsExportDropdownOpen(!isExportDropdownOpen);
  };

  // Add these states (keep existing ones, just make sure they're there)
  // Color picker state - store HSV internally
  const [hsvColor, setHsvColor] = useState({ h: 0, s: 100, v: 100 });
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);
  const activeColorDragRef = useRef<"saturation" | "hue" | null>(null);
  const toggleColorDropdown = () => {
    setIsColorDropdownOpen(!isColorDropdownOpen);
  };

  // Convert HSL to Hex
  // Convert HSV to Hex (instead of HSL)
  // Convert HSV to Hex
  const hsvToHex = useCallback((h: number, s: number, v: number): string => {
    const c = (v / 100) * (s / 100);
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v / 100 - c;

    let r = 0,
      g = 0,
      b = 0;
    if (h >= 0 && h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      g = 0;
      b = c;
    } else if (h >= 300 && h < 360) {
      r = c;
      g = 0;
      b = x;
    }

    const toHex = (n: number) =>
      Math.round((n + m) * 255)
        .toString(16)
        .padStart(2, "0");
    return `${toHex(r)}${toHex(g)}${toHex(b)}`;
  }, []);

  // Convert Hex to HSV
  const hexToHsv = useCallback(
    (hex: string): { h: number; s: number; v: number } => {
      const cleanHex = hex.replace("#", "").padEnd(6, "0");
      const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
      const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
      const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const diff = max - min;

      let h = 0;
      if (diff !== 0) {
        if (max === r) h = 60 * (((g - b) / diff) % 6);
        else if (max === g) h = 60 * ((b - r) / diff + 2);
        else h = 60 * ((r - g) / diff + 4);
        if (h < 0) h += 360;
      }

      const s = max === 0 ? 0 : (diff / max) * 100;
      const v = max * 100;

      // Preserve hue for grayscale colors
      return {
        h: s < 1 ? hsvColor.h : Math.round(h),
        s: Math.round(s),
        v: Math.round(v),
      };
    },
    [hsvColor.h],
  );

  // Initialize HSV from current color when dropdown opens
  useEffect(() => {
    if (isColorDropdownOpen) {
      const hsv = hexToHsv(colorValue);
      setHsvColor(hsv);
    }
  }, [isColorDropdownOpen, colorValue, hexToHsv]);

  // Add this effect to initialize HSV values when colorValue changes
  // Add this effect to initialize HSV values when colorValue changes
  // Add this effect to initialize HSV values when colorValue changes (ONLY from external sources)

  // ADD THE NEW INITIALIZATION EFFECT HERE:
  // Initialize HSV values from current color when component mounts
  // Initialize HSV values from current color when component mounts OR when color picker opens

  // Update hex value whenever HSV changes
  // Update hex value whenever HSV changes

  const isUpdatingFromHueSlider = useRef(false);

  // Hue slider handlers - for click AND drag

  // Saturation/Lightness area handlers - for click AND drag

  // Fix thumb positioning when color dropdown opens
  // Position thumbs correctly when color dropdown opens
  // Color picker dragging
  // Color picker dragging - FIXED VERSION
  // Color picker dragging
  useEffect(() => {
    if (!activeColorDragRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();

      if (
        activeColorDragRef.current === "saturation" &&
        colorPickerRef.current
      ) {
        const rect = colorPickerRef.current.getBoundingClientRect();
        const x = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width),
        );
        const y = Math.max(
          0,
          Math.min(1, (e.clientY - rect.top) / rect.height),
        );

        const newHsv = {
          h: hsvColor.h,
          s: x * 100,
          v: (1 - y) * 100,
        };

        setHsvColor(newHsv);
        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
      }

      if (activeColorDragRef.current === "hue" && hueSliderRef.current) {
        const rect = hueSliderRef.current.getBoundingClientRect();
        const x = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width),
        );

        const newHsv = {
          h: x * 360,
          s: hsvColor.s,
          v: hsvColor.v,
        };

        setHsvColor(newHsv);
        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];

      if (
        activeColorDragRef.current === "saturation" &&
        colorPickerRef.current
      ) {
        const rect = colorPickerRef.current.getBoundingClientRect();
        const x = Math.max(
          0,
          Math.min(1, (touch.clientX - rect.left) / rect.width),
        );
        const y = Math.max(
          0,
          Math.min(1, (touch.clientY - rect.top) / rect.height),
        );

        const newHsv = {
          h: hsvColor.h,
          s: x * 100,
          v: (1 - y) * 100,
        };

        setHsvColor(newHsv);
        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
      }

      if (activeColorDragRef.current === "hue" && hueSliderRef.current) {
        const rect = hueSliderRef.current.getBoundingClientRect();
        const x = Math.max(
          0,
          Math.min(1, (touch.clientX - rect.left) / rect.width),
        );

        const newHsv = {
          h: x * 360,
          s: hsvColor.s,
          v: hsvColor.v,
        };

        setHsvColor(newHsv);
        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
      }
    };

    const handleMouseUp = () => {
      activeColorDragRef.current = null;
    };

    const handleTouchEnd = () => {
      activeColorDragRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove, { passive: false });
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "crosshair";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [activeColorDragRef.current, hsvColor, hsvToHex]);
  // Sync external color changes to HSV
  // Sync external color changes to HSV - but ONLY when not interacting with color picker
  useEffect(() => {
    // Don't sync if we're actively dragging OR if the dropdown just opened
    if (activeColorDragRef.current) return;

    // Only sync when dropdown is closed (external changes only)
    if (!isColorDropdownOpen) {
      const hsv = hexToHsv(colorValue);
      setHsvColor(hsv);
    }
  }, [colorValue, hexToHsv, isColorDropdownOpen]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? event.metaKey : event.ctrlKey;

      // Check if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        console.log("⌨️ Ignoring Cmd+K - user is typing in:", target.tagName);
        return;
      }

      if (modKey && event.key.toLowerCase() === "f") {
        event.preventDefault();
        if (modelTabs && modelTabs.length > 0) {
          onToggleLeftSidebar();
        }
      } else if (modKey && event.key.toLowerCase() === "j") {
        event.preventDefault();
        onToggleRightSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    onToggleLeftSidebar,
    onToggleRightSidebar,
    onToggleModelsSidebar,
    modelTabs,
  ]);
  useEffect(() => {
    // Listen for selection restoration requests from RightSidebar
    const checkForRestoration = setInterval(() => {
      const restoreSelection = (window as any).__restoreSelection;

      if (
        restoreSelection &&
        Array.isArray(restoreSelection) &&
        restoreSelection.length > 0
      ) {
        // console.log("📋 Restoring selection after error:", restoreSelection);

        // Clear the request
        delete (window as any).__restoreSelection;

        // Restore the selection items
        const restoredItems: SelectionItem[] = [];

        restoreSelection.forEach((item: any) => {
          // Find the mesh by object name
          const mesh = sceneRef.current?.children.find(
            (child) =>
              child instanceof THREE.Mesh &&
              child.userData?.isNoahShape &&
              child.userData?.name === item.object,
          ) as THREE.Mesh | undefined;

          if (mesh) {
            const selectionItem: SelectionItem = {
              type: item.type,
              objectName: item.object,
              index: item.index,
              mesh: mesh,
              faceIndex: item.faceIndex || 0,
              browserCoordinates: item.browserCoordinates,
              browserFaceData: item.browserFaceData,
              edgeGroup: item.edgeGroup,
            };

            restoredItems.push(selectionItem);
          }
        });

        setSelectedItems(restoredItems);

        // Signal completion
        setTimeout(() => {
          (window as any).__selectionRestored = true;
        }, 100);
      }
    }, 100);

    return () => clearInterval(checkForRestoration);
  }, []);

  // Around line 460, add these new useEffect hooks right after the scene initialization:

  // Expose camera state functions to parent
  useEffect(() => {
    // Function to get current camera state
    (window as any).__getCurrentCameraState = () => ({
      rotation: { ...currentRotationRef.current },
      distance: cameraControlsRef.current.currentDistance,
      isOrthographic: isOrthographicRef.current,
    });

    // Function to restore camera state
    // Around line 460, update the __restoreCameraState function:
    (window as any).__restoreCameraState = (cameraState: {
      rotation: { x: number; y: number };
      distance: number;
      isOrthographic: boolean;
    }) => {
      // console.log("🎥 Restoring camera state:", cameraState);

      // STOP any ongoing animation
      animationRef.current.isAnimating = false;

      // STOP any inertia/spinning
      cameraControlsRef.current.rotationVelocity = { x: 0, y: 0 };
      cameraControlsRef.current.isRotating = false;

      // Restore rotation
      currentRotationRef.current = { ...cameraState.rotation };
      setCameraRotation(cameraState.rotation);

      // Restore distance
      cameraControlsRef.current.currentDistance = cameraState.distance;

      // Restore orthographic mode
      setIsOrthographic(cameraState.isOrthographic);
      isOrthographicRef.current = cameraState.isOrthographic;

      // Update camera position WITHOUT animation
      updateCameraPosition(cameraState.rotation, cameraState.distance);

      // Update NaviCube WITHOUT animation/transition
      if (naviCubeRef.current) {
        naviCubeRef.current.style.transition = "none";
        naviCubeRef.current.style.transform = `rotateX(${cameraState.rotation.x}deg) rotateY(${cameraState.rotation.y}deg)`;

        // Re-enable transitions after a frame
        requestAnimationFrame(() => {
          if (naviCubeRef.current) {
            naviCubeRef.current.style.transition = "";
          }
        });
      }

      // console.log("✅ Camera state restored");
    };

    return () => {
      delete (window as any).__getCurrentCameraState;
      delete (window as any).__restoreCameraState;
    };
  }, [updateCameraPosition, updateNaviCube, naviCubeRef]); // ADD naviCubeRef here

  const handleTransparencyMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDraggingTransparency(true);

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const paddingPx = 4;
      const usableWidth = rect.width - paddingPx * 2;
      const adjustedX = Math.max(0, Math.min(usableWidth, x - paddingPx));
      const percentage = Math.max(
        0,
        Math.min(100, (adjustedX / usableWidth) * 100),
      );

      // Update both React state AND ref immediately
      setTransparencyValue(percentage);
      dragValuesRef.current.transparency = percentage;
    },
    [],
  );

  // Add this new function here:
  // Add this new function here:
  // Add this new function here:
  // Around line 1200, add before the return statement
  const handleExport = useCallback(
    async (format: string) => {
      if (!noahGeometry || noahGeometry.length === 0) {
        return;
      }

      try {
        const geometryData = noahGeometry[0];

        if (format === ".STL") {
          if (!geometryData.stl_data) {
            return;
          }

          const binaryString = atob(geometryData.stl_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: "application/octet-stream" });
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName || geometryData.name || "model"}.stl`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else if (format === ".STEP") {
          const response = await fetch(`${BACKEND_URL}/api/command`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_input: "EXPORT_STEP_FILE",
              context: {},
              branch_id: currentBranch || "main",
            }),
            signal: AbortSignal.timeout(60000),
          });

          const result = await response.json();

          if (result.success && result.step_data) {
            const binaryString = atob(result.step_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], {
              type: "application/octet-stream",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName || "model"}.step`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }

        setIsExportDropdownOpen(false);
      } catch (error) {
        console.error("Export failed:", error);
      }
    },
    [noahGeometry, fileName, currentBranch],
  );
  // Add transparency toggle function
  // Remove or fix the existing handleTransparencyToggle function
  const handleTransparencyToggle = useCallback(() => {
    // This function is no longer needed since we're using a slider
    // But if you want to keep it for compatibility, it could toggle between 0% and 50%
    const newValue = transparencyValue > 0 ? 0 : 50;
    setTransparencyValue(newValue);
  }, [transparencyValue]);

  // Replace the existing handleTransparencyToggle function with these new handlers:

  const handleTransparencyInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^0-9]/g, "");
      const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
      // Actually update the state immediately, not just the display
      setTransparencyValue(numValue);
    },
    [],
  );

  const handleTransparencyInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/[^0-9]/g, "");
        const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
        setTransparencyValue(numValue);
        input.blur();
      }
    },
    [],
  );

  const handleTransparencyInputFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      event.target.select();
    },
    [],
  );

  const renderFace = (faceName: string, transformClass: string) => (
    <div className={`${styles.face} ${styles[transformClass]}`}>
      <div className={styles.faceGrid}>
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-top-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top-left")}
          onMouseEnter={() => handleHover(faceName, "top-left", true)}
          onMouseLeave={() => handleHover(faceName, "top-left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-top`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top")}
          onMouseEnter={() => handleHover(faceName, "top", true)}
          onMouseLeave={() => handleHover(faceName, "top", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-top-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top-right")}
          onMouseEnter={() => handleHover(faceName, "top-right", true)}
          onMouseLeave={() => handleHover(faceName, "top-right", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "left")}
          onMouseEnter={() => handleHover(faceName, "left", true)}
          onMouseLeave={() => handleHover(faceName, "left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.centerRegion} ${
            hoveredElements.includes(`${faceName}-center`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "center")}
          onMouseEnter={() => handleHover(faceName, "center", true)}
          onMouseLeave={() => handleHover(faceName, "center", false)}
        >
          {faceName.toUpperCase()}
        </div>
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "right")}
          onMouseEnter={() => handleHover(faceName, "right", true)}
          onMouseLeave={() => handleHover(faceName, "right", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-bottom-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom-left")}
          onMouseEnter={() => handleHover(faceName, "bottom-left", true)}
          onMouseLeave={() => handleHover(faceName, "bottom-left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-bottom`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom")}
          onMouseEnter={() => handleHover(faceName, "bottom", true)}
          onMouseLeave={() => handleHover(faceName, "bottom", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-bottom-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom-right")}
          onMouseEnter={() => handleHover(faceName, "bottom-right", true)}
          onMouseLeave={() => handleHover(faceName, "bottom-right", false)}
        />
      </div>
    </div>
  );

  // --- INJECT EXACT RIGHT SIDEBAR SHARE LOGIC ---
  const generateShareToken = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  const handleSharePrivateClick = async () => {
    // CRITICAL FIX: Use activeTabId so it targets the correct tab's document
    const targetId = activeTabId || projectId;
    if (!targetId) return;

    try {
      await updateDoc(doc(db, "projects", targetId), {
        isPublic: false,
      });

      setLocalIsPublic(false);

      if (onShareStatusChange) {
        onShareStatusChange();
      }
    } catch (error) {
      console.error("Error setting project to private:", error);
    }
  };

  const handleSharePublicClick = async () => {
    // CRITICAL FIX: Use activeTabId so it targets the correct tab's document
    const targetId = activeTabId || projectId;
    if (!targetId) return;

    try {
      let token = localShareToken;

      if (!token) {
        token = generateShareToken();
        setLocalShareToken(token);
      }

      await updateDoc(doc(db, "projects", targetId), {
        isPublic: true,
        shareToken: token,
      });

      setLocalIsPublic(true);

      const shareUrl = `${window.location.origin}/share/${token}`;
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedShare(true);
      setTimeout(() => {
        setIsCopiedShare(false);
      }, 2000);

      if (onShareStatusChange) {
        onShareStatusChange();
      }
    } catch (error) {
      console.error("Error setting project to public:", error);
    }
  };

  const handleCopyShareLink = () => {
    if (localShareToken) {
      const shareUrl = `${window.location.origin}/share/${localShareToken}`;
      navigator.clipboard.writeText(shareUrl);
      setIsCopiedShare(true);
      setTimeout(() => {
        setIsCopiedShare(false);
      }, 2000);
    }
  };
  // --- END LOGIC ---

  const toggleShareDropdown = () => {
    setIsShareDropdownOpen(!isShareDropdownOpen);
  };

  // console.log("🔍 View Debug:", {
  //   modelTabs,
  //   modelTabsLength: modelTabs?.length,
  //   activeTabId,
  //   hasOnTabClick: !!onTabClick,
  // });

  // Update the return statement's JSX:
  return (
    <div
      className={`${styles.view} ${
        !modelTabs || modelTabs.length === 0
          ? styles.viewEmptyState
          : styles.viewWithTabs
      }`}
    >
      {/* NEW: Model tabs at the top - always reserve space */}
      {/* {modelTabs && modelTabs.length > 0 && (
        <div className={styles.tabBarWrapper}>
          <ModelTabs
            tabs={modelTabs}
            activeTabId={activeTabId || null}
            onTabClick={onTabClick || (() => {})}
            onTabRename={onTabRename}
            onTabClose={onTabClose || (() => {})}
            onCloseAll={onCloseAllTabs || (() => {})}
            onNewModel={onNewModel}
            isLoading={isCommandExecuting || isAnyButtonLoading}
            isCreatingNewModel={isCreatingNewModel}
          />
        </div>
      )} */}

      {/* Always mount the 3D viewport for Three.js */}
      <div
        ref={mountRef}
        className={styles.viewport3D}
        data-loading={isLoading}
        style={{
          position: "absolute",
          top: 0, // CHANGED FROM: modelTabs && modelTabs.length > 0 ? "3rem" : "0"
          left: 0,
          width: "100%",
          height: "100%", // CHANGED FROM: modelTabs && modelTabs.length > 0 ? "calc(100% - 3rem)" : "100%"
        }}
      />

      {/* Show empty state when no tabs exist */}
      {(!modelTabs || modelTabs.length === 0) && (
        <div className={styles.emptyStateContainer}>
          <img
            src={
              document.documentElement.classList.contains("dark-theme")
                ? noahDisabledEmptyDark
                : noahDisabledEmptyLight
            }
            alt="Noah"
            className={styles.emptyStateLogo}
          />
        </div>
      )}

      {/* Show waiting state when tab exists but waiting for first prompt */}
      {modelTabs &&
        modelTabs.length > 0 &&
        (!noahGeometry || noahGeometry.length === 0) &&
        isWaitingForFirstPrompt && (
          <div className={styles.emptyStateContainer}>
            <img
              src={getIcon(noahWaitingLight, noahWaitingDark)}
              alt="Noah Waiting"
              className={styles.emptyStateLogo}
            />
          </div>
        )}

      {/* Show loading state when tab exists and actively loading */}
      {modelTabs &&
        modelTabs.length > 0 &&
        (!noahGeometry || noahGeometry.length === 0) &&
        !isWaitingForFirstPrompt && (
          <div className={styles.emptyStateContainer}>
            <img
              src={getIcon(noahDisabledLight, noahDisabledDark)}
              alt="Noah Loading"
              className={styles.emptyStateLogo}
            />
          </div>
        )}

      {/* Only show NaviCube and color picker when we have geometry */}
      {noahGeometry && noahGeometry.length > 0 && (
        <>
          {/* NaviCube - BOTTOM RIGHT */}
          <div
            className={styles.naviCube}
            style={{ top: "2rem", right: "2rem" }}
          >
            <div
              ref={naviCubeRef}
              className={styles.cube}
              style={{
                transform: `rotateX(${cameraRotation.x}deg) rotateY(${cameraRotation.y}deg)`,
              }}
            >
              {renderFace("front", "frontFace")}
              {renderFace("back", "backFace")}
              {renderFace("right", "rightFace")}
              {renderFace("left", "leftFace")}
              {renderFace("top", "topFace")}
              {renderFace("bottom", "bottomFace")}
            </div>
          </div>

          {/* Bottom Left - Color picker button - HIDE FOR SHARED MODELS */}
          {!isActiveTabShared && (
            <div className={styles.bottomLeft}>
              <div
                className={styles.colorPickerContainer}
                ref={colorDropdownRef}
              >
                <button
                  className={`${styles.colorButton} ${
                    isColorDropdownOpen ? styles.colorButtonActive : ""
                  }`}
                  onClick={toggleColorDropdown}
                  onMouseEnter={() => setShowColorTooltip(true)}
                  onMouseLeave={() => setShowColorTooltip(false)}
                >
                  <div
                    className={styles.colorSquare}
                    style={{ backgroundColor: `#${colorValue}` }}
                  />
                </button>

                {showColorTooltip && !isColorDropdownOpen && (
                  <div className={styles.colorTooltip}>Color</div>
                )}

                {isColorDropdownOpen && (
                  <div className={styles.colorDropdown}>
                    {/* Saturation/Value picker */}
                    <div
                      ref={colorPickerRef}
                      className={styles.saturationLightnessArea}
                      style={{
                        background: `
                    linear-gradient(to top, #000, transparent),
                    linear-gradient(to right, #fff, hsl(${hsvColor.h}, 100%, 50%))
                  `,
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        activeColorDragRef.current = "saturation";
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.max(
                          0,
                          Math.min(1, (e.clientX - rect.left) / rect.width),
                        );
                        const y = Math.max(
                          0,
                          Math.min(1, (e.clientY - rect.top) / rect.height),
                        );
                        const newHsv = {
                          h: hsvColor.h,
                          s: x * 100,
                          v: (1 - y) * 100,
                        };
                        setHsvColor(newHsv);
                        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        activeColorDragRef.current = "saturation";
                        const touch = e.touches[0];
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.max(
                          0,
                          Math.min(1, (touch.clientX - rect.left) / rect.width),
                        );
                        const y = Math.max(
                          0,
                          Math.min(1, (touch.clientY - rect.top) / rect.height),
                        );
                        const newHsv = {
                          h: hsvColor.h,
                          s: x * 100,
                          v: (1 - y) * 100,
                        };
                        setHsvColor(newHsv);
                        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
                      }}
                    >
                      <div
                        className={styles.colorThumb}
                        style={{
                          left: `${hsvColor.s}%`,
                          top: `${100 - hsvColor.v}%`,
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </div>

                    {/* Hue slider */}
                    <div
                      ref={hueSliderRef}
                      className={styles.hueSlider}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        activeColorDragRef.current = "hue";
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.max(
                          0,
                          Math.min(1, (e.clientX - rect.left) / rect.width),
                        );
                        const newHsv = {
                          h: x * 360,
                          s: hsvColor.s,
                          v: hsvColor.v,
                        };
                        setHsvColor(newHsv);
                        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        activeColorDragRef.current = "hue";
                        const touch = e.touches[0];
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = Math.max(
                          0,
                          Math.min(1, (touch.clientX - rect.left) / rect.width),
                        );
                        const newHsv = {
                          h: x * 360,
                          s: hsvColor.s,
                          v: hsvColor.v,
                        };
                        setHsvColor(newHsv);
                        setColorValue(hsvToHex(newHsv.h, newHsv.s, newHsv.v));
                      }}
                    >
                      <div
                        className={styles.hueThumb}
                        style={{
                          left: `${(hsvColor.h / 360) * 100}%`,
                        }}
                      />
                    </div>

                    {/* Color input field */}
                    <div className={styles.colorInputContainer}>
                      <span className={styles.hashSymbol}>#</span>
                      <input
                        ref={colorInputRef}
                        type="text"
                        value={colorValue.toUpperCase()}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^0-9A-Fa-f]/g, "")
                            .toUpperCase();
                          if (value.length <= 6) {
                            setColorValue(value);
                            if (value.length === 6) {
                              const hsv = hexToHsv(value);
                              setHsvColor(hsv);
                            }
                          }
                        }}
                        onKeyDown={handleColorInputKeyDown}
                        onFocus={(e) => e.target.select()}
                        className={styles.colorInput}
                        maxLength={6}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Bottom Right - Download button */}
          {/* Bottom Right - Share and Download buttons */}
          {/* Bottom Right - Share and Download buttons */}
          <div className={styles.bottomRight} style={{ gap: "0" }}>
            {!isActiveTabShared && (
              <div className={styles.shareContainer} ref={shareDropdownRef}>
                <button
                  onClick={() => setIsShareDropdownOpen(!isShareDropdownOpen)}
                  style={{
                    backgroundColor: "var(--color-background)",
                    color: "var(--color-black)",
                    height: "2rem",
                    padding: "0 0.5rem",
                    borderRadius: "0",
                    border: "1px solid var(--color-border)",
                    borderRight: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                    boxSizing: "border-box",
                  }}
                >
                  <img
                    src={shareIcon}
                    alt="Share"
                    style={{ width: "0.875rem", height: "0.875rem" }}
                  />
                  <span
                    style={{
                      fontSize: "0.875rem",
                      fontFamily: "inherit",
                      fontWeight: 400,
                    }}
                  >
                    Share
                  </span>
                </button>

                {isShareDropdownOpen && (
                  <div className={styles.shareDropdown}>
                    <div
                      className={`${styles.shareOption} ${
                        !localIsPublic ? styles.shareOptionSelected : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSharePrivateClick();
                      }}
                    >
                      <div className={styles.shareOptionContent}>
                        <div className={styles.shareOptionMain}>Private</div>
                        <div className={styles.shareOptionSub}>
                          Only you can edit
                        </div>
                      </div>
                    </div>
                    <div
                      className={`${styles.shareOption} ${
                        localIsPublic ? styles.shareOptionSelected : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSharePublicClick();
                      }}
                    >
                      <div className={styles.shareOptionContent}>
                        <div className={styles.shareOptionMain}>Public</div>
                        <div className={styles.shareOptionSub}>
                          Anyone with the link can view
                        </div>
                        {localIsPublic && localShareToken && (
                          <div
                            className={styles.shareLinkBox}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyShareLink();
                            }}
                          >
                            <div className={styles.shareLinkText}>
                              {`${window.location.origin}/share/${localShareToken}`}
                            </div>
                            <div className={styles.copyShareIcon}>
                              <img
                                src={
                                  isCopiedShare ? checkIconShare : copyIconShare
                                }
                                alt={isCopiedShare ? "Copied" : "Copy"}
                                className={styles.copyIcon}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={styles.downloadContainer} ref={exportDropdownRef}>
              <button
                onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                style={{
                  backgroundColor: "var(--color-black)",
                  color: "var(--color-white)",
                  height: "2rem",
                  padding: "0 0.5rem",
                  borderRadius: "0",
                  border: "1px solid var(--color-border)",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                }}
              >
                <img
                  src={exportIcon}
                  alt="Download"
                  style={{ width: "0.875rem", height: "0.875rem" }}
                />
                <span
                  style={{
                    fontSize: "0.875rem",
                    fontFamily: "inherit",
                    fontWeight: 400,
                  }}
                >
                  Download
                </span>
              </button>

              {isExportDropdownOpen && (
                <div className={styles.downloadDropdown}>
                  <div
                    className={styles.downloadOption}
                    onClick={() => handleExport(".STL")}
                  >
                    <div className={styles.downloadOptionMain}>.STL</div>
                    <div className={styles.downloadOptionSub}>3D Printing</div>
                  </div>
                  <div
                    className={styles.downloadOption}
                    onClick={() => handleExport(".STEP")}
                  >
                    <div className={styles.downloadOptionMain}>.STEP</div>
                    <div className={styles.downloadOptionSub}>CAD Exchange</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default View;
